var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
class YscApiConsumer {
  constructor(client, baseURL) {
    __publicField(this, "_client");
    __publicField(this, "_baseURL");
    __publicField(this, "send", async (request) => {
      const { url, options } = request.prepare(this._baseURL);
      try {
        const data = await this._client(url, options);
        return request.parseResult(data);
      } catch (error) {
        if (request.silentCatch) {
          return request.parseResult();
        }
        throw error;
      }
    });
    this._client = client;
    this._baseURL = baseURL;
  }
}
class YscApiRequest {
  constructor() {
    __publicField(this, "silentCatch", false);
  }
  prepare(baseURL) {
    return getRequestOptions({
      url: this.getEndpointURL(baseURL),
      method: this.getMethod()
    });
  }
}
function isPlainObject(value) {
  if (value === null || typeof value !== "object") {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== null && prototype !== Object.prototype && Object.getPrototypeOf(prototype) !== null) {
    return false;
  }
  if (Symbol.iterator in value) {
    return false;
  }
  if (Symbol.toStringTag in value) {
    return Object.prototype.toString.call(value) === "[object Module]";
  }
  return true;
}
function _defu(baseObject, defaults, namespace = ".", merger) {
  if (!isPlainObject(defaults)) {
    return _defu(baseObject, {}, namespace, merger);
  }
  const object = Object.assign({}, defaults);
  for (const key in baseObject) {
    if (key === "__proto__" || key === "constructor") {
      continue;
    }
    const value = baseObject[key];
    if (value === null || value === void 0) {
      continue;
    }
    if (merger && merger(object, key, value, namespace)) {
      continue;
    }
    if (Array.isArray(value) && Array.isArray(object[key])) {
      object[key] = [...value, ...object[key]];
    } else if (isPlainObject(value) && isPlainObject(object[key])) {
      object[key] = _defu(
        value,
        object[key],
        (namespace ? `${namespace}.` : "") + key.toString(),
        merger
      );
    } else {
      object[key] = value;
    }
  }
  return object;
}
function createDefu(merger) {
  return (...arguments_) => (
    // eslint-disable-next-line unicorn/no-array-reduce
    arguments_.reduce((p, c) => _defu(p, c, "", merger), {})
  );
}
const defu = createDefu();
class YscApiRequestWithPayload extends YscApiRequest {
  constructor({ payload } = {}) {
    super();
    __publicField(this, "_payload");
    this._payload = payload;
  }
  prepare(baseURL) {
    return defu(
      { options: { body: this.formatPayload() } },
      super.prepare(baseURL)
    );
  }
  formatPayload() {
    return this._payload;
  }
}
class YscApiRequestCreate extends YscApiRequestWithPayload {
  getMethod() {
    return "POST";
  }
}
class YscApiRequestCreateFormUrlEncoded extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  prepare(baseURL) {
    return defu(
      { options: { headers: { "Content-Type": "application/x-www-form-urlencoded" } } },
      super.prepare(baseURL)
    );
  }
}
class YscApiRequestCreateMultipartFormData extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  prepare(baseURL) {
    return defu(
      { options: { headers: { "Content-Type": "multipart/form-data" } } },
      super.prepare(baseURL)
    );
  }
}
class YscApiRequestDelete extends YscApiRequestWithPayload {
  getMethod() {
    return "DELETE";
  }
}
class YscApiRequestWithQuery extends YscApiRequest {
  constructor({ query } = {}) {
    super();
    __publicField(this, "_query");
    this._query = query;
  }
  prepare(baseURL) {
    return super.prepare(baseURL);
  }
}
class YscApiRequestRead extends YscApiRequestWithQuery {
  getMethod() {
    return "GET";
  }
}
class YscApiRequestUpdate extends YscApiRequestWithPayload {
  getMethod() {
    return "PATCH";
  }
}
class YscApiRequestUpdateMultipartFormData extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  prepare(baseURL) {
    return defu(
      { options: { headers: { "Content-Type": "multipart/form-data" } } },
      super.prepare(baseURL)
    );
  }
}
const formatDistanceLocale = {
  lessThanXSeconds: {
    one: "less than a second",
    other: "less than {{count}} seconds"
  },
  xSeconds: {
    one: "1 second",
    other: "{{count}} seconds"
  },
  halfAMinute: "half a minute",
  lessThanXMinutes: {
    one: "less than a minute",
    other: "less than {{count}} minutes"
  },
  xMinutes: {
    one: "1 minute",
    other: "{{count}} minutes"
  },
  aboutXHours: {
    one: "about 1 hour",
    other: "about {{count}} hours"
  },
  xHours: {
    one: "1 hour",
    other: "{{count}} hours"
  },
  xDays: {
    one: "1 day",
    other: "{{count}} days"
  },
  aboutXWeeks: {
    one: "about 1 week",
    other: "about {{count}} weeks"
  },
  xWeeks: {
    one: "1 week",
    other: "{{count}} weeks"
  },
  aboutXMonths: {
    one: "about 1 month",
    other: "about {{count}} months"
  },
  xMonths: {
    one: "1 month",
    other: "{{count}} months"
  },
  aboutXYears: {
    one: "about 1 year",
    other: "about {{count}} years"
  },
  xYears: {
    one: "1 year",
    other: "{{count}} years"
  },
  overXYears: {
    one: "over 1 year",
    other: "over {{count}} years"
  },
  almostXYears: {
    one: "almost 1 year",
    other: "almost {{count}} years"
  }
};
const formatDistance = (token, count, options) => {
  let result;
  const tokenValue = formatDistanceLocale[token];
  if (typeof tokenValue === "string") {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace("{{count}}", count.toString());
  }
  if (options == null ? void 0 : options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return "in " + result;
    } else {
      return result + " ago";
    }
  }
  return result;
};
function buildFormatLongFn(args) {
  return (options = {}) => {
    const width = options.width ? String(options.width) : args.defaultWidth;
    const format2 = args.formats[width] || args.formats[args.defaultWidth];
    return format2;
  };
}
const dateFormats = {
  full: "EEEE, MMMM do, y",
  long: "MMMM do, y",
  medium: "MMM d, y",
  short: "MM/dd/yyyy"
};
const timeFormats = {
  full: "h:mm:ss a zzzz",
  long: "h:mm:ss a z",
  medium: "h:mm:ss a",
  short: "h:mm a"
};
const dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: "{{date}}, {{time}}",
  short: "{{date}}, {{time}}"
};
const formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: "full"
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: "full"
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: "full"
  })
};
const formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: "P"
};
const formatRelative = (token, _date, _baseDate, _options) => formatRelativeLocale[token];
function buildLocalizeFn(args) {
  return (value, options) => {
    const context = (options == null ? void 0 : options.context) ? String(options.context) : "standalone";
    let valuesArray;
    if (context === "formatting" && args.formattingValues) {
      const defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      const width = (options == null ? void 0 : options.width) ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      const defaultWidth = args.defaultWidth;
      const width = (options == null ? void 0 : options.width) ? String(options.width) : args.defaultWidth;
      valuesArray = args.values[width] || args.values[defaultWidth];
    }
    const index = args.argumentCallback ? args.argumentCallback(value) : value;
    return valuesArray[index];
  };
}
const eraValues = {
  narrow: ["B", "A"],
  abbreviated: ["BC", "AD"],
  wide: ["Before Christ", "Anno Domini"]
};
const quarterValues = {
  narrow: ["1", "2", "3", "4"],
  abbreviated: ["Q1", "Q2", "Q3", "Q4"],
  wide: ["1st quarter", "2nd quarter", "3rd quarter", "4th quarter"]
};
const monthValues = {
  narrow: ["J", "F", "M", "A", "M", "J", "J", "A", "S", "O", "N", "D"],
  abbreviated: [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec"
  ],
  wide: [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ]
};
const dayValues = {
  narrow: ["S", "M", "T", "W", "T", "F", "S"],
  short: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
  abbreviated: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
  wide: [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday"
  ]
};
const dayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night"
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night"
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "morning",
    afternoon: "afternoon",
    evening: "evening",
    night: "night"
  }
};
const formattingDayPeriodValues = {
  narrow: {
    am: "a",
    pm: "p",
    midnight: "mi",
    noon: "n",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night"
  },
  abbreviated: {
    am: "AM",
    pm: "PM",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night"
  },
  wide: {
    am: "a.m.",
    pm: "p.m.",
    midnight: "midnight",
    noon: "noon",
    morning: "in the morning",
    afternoon: "in the afternoon",
    evening: "in the evening",
    night: "at night"
  }
};
const ordinalNumber = (dirtyNumber, _options) => {
  const number = Number(dirtyNumber);
  const rem100 = number % 100;
  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + "st";
      case 2:
        return number + "nd";
      case 3:
        return number + "rd";
    }
  }
  return number + "th";
};
const localize = {
  ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: "wide"
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: "wide",
    argumentCallback: (quarter) => quarter - 1
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: "wide"
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: "wide"
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: "wide",
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: "wide"
  })
};
function buildMatchFn(args) {
  return (string, options = {}) => {
    const width = options.width;
    const matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    const matchResult = string.match(matchPattern);
    if (!matchResult) {
      return null;
    }
    const matchedString = matchResult[0];
    const parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    const key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, (pattern) => pattern.test(matchedString)) : (
      // [TODO] -- I challenge you to fix the type
      findKey(parsePatterns, (pattern) => pattern.test(matchedString))
    );
    let value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? (
      // [TODO] -- I challenge you to fix the type
      options.valueCallback(value)
    ) : value;
    const rest = string.slice(matchedString.length);
    return { value, rest };
  };
}
function findKey(object, predicate) {
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key) && predicate(object[key])) {
      return key;
    }
  }
  return void 0;
}
function findIndex(array, predicate) {
  for (let key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
  return void 0;
}
function buildMatchPatternFn(args) {
  return (string, options = {}) => {
    const matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    const matchedString = matchResult[0];
    const parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    let value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    const rest = string.slice(matchedString.length);
    return { value, rest };
  };
}
const matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
const parseOrdinalNumberPattern = /\d+/i;
const matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
const parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
const matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
const parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
const matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
const parseMonthPatterns = {
  narrow: [
    /^j/i,
    /^f/i,
    /^m/i,
    /^a/i,
    /^m/i,
    /^j/i,
    /^j/i,
    /^a/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i
  ],
  any: [
    /^ja/i,
    /^f/i,
    /^mar/i,
    /^ap/i,
    /^may/i,
    /^jun/i,
    /^jul/i,
    /^au/i,
    /^s/i,
    /^o/i,
    /^n/i,
    /^d/i
  ]
};
const matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
const parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
const matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
const parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
const match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: (value) => parseInt(value, 10)
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseEraPatterns,
    defaultParseWidth: "any"
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: "any",
    valueCallback: (index) => index + 1
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: "any"
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: "wide",
    parsePatterns: parseDayPatterns,
    defaultParseWidth: "any"
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: "any",
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: "any"
  })
};
const enUS = {
  code: "en-US",
  formatDistance,
  formatLong,
  formatRelative,
  localize,
  match,
  options: {
    weekStartsOn: 0,
    firstWeekContainsDate: 1
  }
};
let defaultOptions = {};
function getDefaultOptions$1() {
  return defaultOptions;
}
const millisecondsInWeek = 6048e5;
const millisecondsInDay = 864e5;
const constructFromSymbol = Symbol.for("constructDateFrom");
function constructFrom(date, value) {
  if (typeof date === "function") return date(value);
  if (date && typeof date === "object" && constructFromSymbol in date)
    return date[constructFromSymbol](value);
  if (date instanceof Date) return new date.constructor(value);
  return new Date(value);
}
function toDate$1(argument, context) {
  return constructFrom(context || argument, argument);
}
function getTimezoneOffsetInMilliseconds$1(date) {
  const _date = toDate$1(date);
  const utcDate = new Date(
    Date.UTC(
      _date.getFullYear(),
      _date.getMonth(),
      _date.getDate(),
      _date.getHours(),
      _date.getMinutes(),
      _date.getSeconds(),
      _date.getMilliseconds()
    )
  );
  utcDate.setUTCFullYear(_date.getFullYear());
  return +date - +utcDate;
}
function normalizeDates(context, ...dates) {
  const normalize = constructFrom.bind(
    null,
    dates.find((date) => typeof date === "object")
  );
  return dates.map(normalize);
}
function startOfDay(date, options) {
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  _date.setHours(0, 0, 0, 0);
  return _date;
}
function differenceInCalendarDays(laterDate, earlierDate, options) {
  const [laterDate_, earlierDate_] = normalizeDates(
    options == null ? void 0 : options.in,
    laterDate,
    earlierDate
  );
  const laterStartOfDay = startOfDay(laterDate_);
  const earlierStartOfDay = startOfDay(earlierDate_);
  const laterTimestamp = +laterStartOfDay - getTimezoneOffsetInMilliseconds$1(laterStartOfDay);
  const earlierTimestamp = +earlierStartOfDay - getTimezoneOffsetInMilliseconds$1(earlierStartOfDay);
  return Math.round((laterTimestamp - earlierTimestamp) / millisecondsInDay);
}
function startOfYear(date, options) {
  const date_ = toDate$1(date, options == null ? void 0 : options.in);
  date_.setFullYear(date_.getFullYear(), 0, 1);
  date_.setHours(0, 0, 0, 0);
  return date_;
}
function getDayOfYear(date, options) {
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  const diff = differenceInCalendarDays(_date, startOfYear(_date));
  const dayOfYear = diff + 1;
  return dayOfYear;
}
function startOfWeek(date, options) {
  var _a, _b, _c, _d;
  const defaultOptions2 = getDefaultOptions$1();
  const weekStartsOn = (options == null ? void 0 : options.weekStartsOn) ?? ((_b = (_a = options == null ? void 0 : options.locale) == null ? void 0 : _a.options) == null ? void 0 : _b.weekStartsOn) ?? defaultOptions2.weekStartsOn ?? ((_d = (_c = defaultOptions2.locale) == null ? void 0 : _c.options) == null ? void 0 : _d.weekStartsOn) ?? 0;
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  const day = _date.getDay();
  const diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  _date.setDate(_date.getDate() - diff);
  _date.setHours(0, 0, 0, 0);
  return _date;
}
function startOfISOWeek(date, options) {
  return startOfWeek(date, { ...options, weekStartsOn: 1 });
}
function getISOWeekYear(date, options) {
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  const year = _date.getFullYear();
  const fourthOfJanuaryOfNextYear = constructFrom(_date, 0);
  fourthOfJanuaryOfNextYear.setFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfISOWeek(fourthOfJanuaryOfNextYear);
  const fourthOfJanuaryOfThisYear = constructFrom(_date, 0);
  fourthOfJanuaryOfThisYear.setFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfISOWeek(fourthOfJanuaryOfThisYear);
  if (_date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (_date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}
function startOfISOWeekYear(date, options) {
  const year = getISOWeekYear(date, options);
  const fourthOfJanuary = constructFrom(date, 0);
  fourthOfJanuary.setFullYear(year, 0, 4);
  fourthOfJanuary.setHours(0, 0, 0, 0);
  return startOfISOWeek(fourthOfJanuary);
}
function getISOWeek(date, options) {
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  const diff = +startOfISOWeek(_date) - +startOfISOWeekYear(_date);
  return Math.round(diff / millisecondsInWeek) + 1;
}
function getWeekYear(date, options) {
  var _a, _b, _c, _d;
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  const year = _date.getFullYear();
  const defaultOptions2 = getDefaultOptions$1();
  const firstWeekContainsDate = (options == null ? void 0 : options.firstWeekContainsDate) ?? ((_b = (_a = options == null ? void 0 : options.locale) == null ? void 0 : _a.options) == null ? void 0 : _b.firstWeekContainsDate) ?? defaultOptions2.firstWeekContainsDate ?? ((_d = (_c = defaultOptions2.locale) == null ? void 0 : _c.options) == null ? void 0 : _d.firstWeekContainsDate) ?? 1;
  const firstWeekOfNextYear = constructFrom((options == null ? void 0 : options.in) || date, 0);
  firstWeekOfNextYear.setFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setHours(0, 0, 0, 0);
  const startOfNextYear = startOfWeek(firstWeekOfNextYear, options);
  const firstWeekOfThisYear = constructFrom((options == null ? void 0 : options.in) || date, 0);
  firstWeekOfThisYear.setFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setHours(0, 0, 0, 0);
  const startOfThisYear = startOfWeek(firstWeekOfThisYear, options);
  if (+_date >= +startOfNextYear) {
    return year + 1;
  } else if (+_date >= +startOfThisYear) {
    return year;
  } else {
    return year - 1;
  }
}
function startOfWeekYear(date, options) {
  var _a, _b, _c, _d;
  const defaultOptions2 = getDefaultOptions$1();
  const firstWeekContainsDate = (options == null ? void 0 : options.firstWeekContainsDate) ?? ((_b = (_a = options == null ? void 0 : options.locale) == null ? void 0 : _a.options) == null ? void 0 : _b.firstWeekContainsDate) ?? defaultOptions2.firstWeekContainsDate ?? ((_d = (_c = defaultOptions2.locale) == null ? void 0 : _c.options) == null ? void 0 : _d.firstWeekContainsDate) ?? 1;
  const year = getWeekYear(date, options);
  const firstWeek = constructFrom((options == null ? void 0 : options.in) || date, 0);
  firstWeek.setFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setHours(0, 0, 0, 0);
  const _date = startOfWeek(firstWeek, options);
  return _date;
}
function getWeek(date, options) {
  const _date = toDate$1(date, options == null ? void 0 : options.in);
  const diff = +startOfWeek(_date, options) - +startOfWeekYear(_date, options);
  return Math.round(diff / millisecondsInWeek) + 1;
}
function addLeadingZeros$1(number, targetLength) {
  const sign = number < 0 ? "-" : "";
  const output = Math.abs(number).toString().padStart(targetLength, "0");
  return sign + output;
}
const lightFormatters = {
  // Year
  y(date, token) {
    const signedYear = date.getFullYear();
    const year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros$1(token === "yy" ? year % 100 : year, token.length);
  },
  // Month
  M(date, token) {
    const month = date.getMonth();
    return token === "M" ? String(month + 1) : addLeadingZeros$1(month + 1, 2);
  },
  // Day of the month
  d(date, token) {
    return addLeadingZeros$1(date.getDate(), token.length);
  },
  // AM or PM
  a(date, token) {
    const dayPeriodEnumValue = date.getHours() / 12 >= 1 ? "pm" : "am";
    switch (token) {
      case "a":
      case "aa":
        return dayPeriodEnumValue.toUpperCase();
      case "aaa":
        return dayPeriodEnumValue;
      case "aaaaa":
        return dayPeriodEnumValue[0];
      case "aaaa":
      default:
        return dayPeriodEnumValue === "am" ? "a.m." : "p.m.";
    }
  },
  // Hour [1-12]
  h(date, token) {
    return addLeadingZeros$1(date.getHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H(date, token) {
    return addLeadingZeros$1(date.getHours(), token.length);
  },
  // Minute
  m(date, token) {
    return addLeadingZeros$1(date.getMinutes(), token.length);
  },
  // Second
  s(date, token) {
    return addLeadingZeros$1(date.getSeconds(), token.length);
  },
  // Fraction of second
  S(date, token) {
    const numberOfDigits = token.length;
    const milliseconds = date.getMilliseconds();
    const fractionalSeconds = Math.trunc(
      milliseconds * Math.pow(10, numberOfDigits - 3)
    );
    return addLeadingZeros$1(fractionalSeconds, token.length);
  }
};
const dayPeriodEnum = {
  midnight: "midnight",
  noon: "noon",
  morning: "morning",
  afternoon: "afternoon",
  evening: "evening",
  night: "night"
};
const formatters$1 = {
  // Era
  G: function(date, token, localize2) {
    const era = date.getFullYear() > 0 ? 1 : 0;
    switch (token) {
      // AD, BC
      case "G":
      case "GG":
      case "GGG":
        return localize2.era(era, { width: "abbreviated" });
      // A, B
      case "GGGGG":
        return localize2.era(era, { width: "narrow" });
      // Anno Domini, Before Christ
      case "GGGG":
      default:
        return localize2.era(era, { width: "wide" });
    }
  },
  // Year
  y: function(date, token, localize2) {
    if (token === "yo") {
      const signedYear = date.getFullYear();
      const year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize2.ordinalNumber(year, { unit: "year" });
    }
    return lightFormatters.y(date, token);
  },
  // Local week-numbering year
  Y: function(date, token, localize2, options) {
    const signedWeekYear = getWeekYear(date, options);
    const weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear;
    if (token === "YY") {
      const twoDigitYear = weekYear % 100;
      return addLeadingZeros$1(twoDigitYear, 2);
    }
    if (token === "Yo") {
      return localize2.ordinalNumber(weekYear, { unit: "year" });
    }
    return addLeadingZeros$1(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function(date, token) {
    const isoWeekYear = getISOWeekYear(date);
    return addLeadingZeros$1(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function(date, token) {
    const year = date.getFullYear();
    return addLeadingZeros$1(year, token.length);
  },
  // Quarter
  Q: function(date, token, localize2) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "Q":
        return String(quarter);
      // 01, 02, 03, 04
      case "QQ":
        return addLeadingZeros$1(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "Qo":
        return localize2.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "QQQ":
        return localize2.quarter(quarter, {
          width: "abbreviated",
          context: "formatting"
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "QQQQQ":
        return localize2.quarter(quarter, {
          width: "narrow",
          context: "formatting"
        });
      // 1st quarter, 2nd quarter, ...
      case "QQQQ":
      default:
        return localize2.quarter(quarter, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // Stand-alone quarter
  q: function(date, token, localize2) {
    const quarter = Math.ceil((date.getMonth() + 1) / 3);
    switch (token) {
      // 1, 2, 3, 4
      case "q":
        return String(quarter);
      // 01, 02, 03, 04
      case "qq":
        return addLeadingZeros$1(quarter, 2);
      // 1st, 2nd, 3rd, 4th
      case "qo":
        return localize2.ordinalNumber(quarter, { unit: "quarter" });
      // Q1, Q2, Q3, Q4
      case "qqq":
        return localize2.quarter(quarter, {
          width: "abbreviated",
          context: "standalone"
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)
      case "qqqqq":
        return localize2.quarter(quarter, {
          width: "narrow",
          context: "standalone"
        });
      // 1st quarter, 2nd quarter, ...
      case "qqqq":
      default:
        return localize2.quarter(quarter, {
          width: "wide",
          context: "standalone"
        });
    }
  },
  // Month
  M: function(date, token, localize2) {
    const month = date.getMonth();
    switch (token) {
      case "M":
      case "MM":
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th
      case "Mo":
        return localize2.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "MMM":
        return localize2.month(month, {
          width: "abbreviated",
          context: "formatting"
        });
      // J, F, ..., D
      case "MMMMM":
        return localize2.month(month, {
          width: "narrow",
          context: "formatting"
        });
      // January, February, ..., December
      case "MMMM":
      default:
        return localize2.month(month, { width: "wide", context: "formatting" });
    }
  },
  // Stand-alone month
  L: function(date, token, localize2) {
    const month = date.getMonth();
    switch (token) {
      // 1, 2, ..., 12
      case "L":
        return String(month + 1);
      // 01, 02, ..., 12
      case "LL":
        return addLeadingZeros$1(month + 1, 2);
      // 1st, 2nd, ..., 12th
      case "Lo":
        return localize2.ordinalNumber(month + 1, { unit: "month" });
      // Jan, Feb, ..., Dec
      case "LLL":
        return localize2.month(month, {
          width: "abbreviated",
          context: "standalone"
        });
      // J, F, ..., D
      case "LLLLL":
        return localize2.month(month, {
          width: "narrow",
          context: "standalone"
        });
      // January, February, ..., December
      case "LLLL":
      default:
        return localize2.month(month, { width: "wide", context: "standalone" });
    }
  },
  // Local week of year
  w: function(date, token, localize2, options) {
    const week = getWeek(date, options);
    if (token === "wo") {
      return localize2.ordinalNumber(week, { unit: "week" });
    }
    return addLeadingZeros$1(week, token.length);
  },
  // ISO week of year
  I: function(date, token, localize2) {
    const isoWeek = getISOWeek(date);
    if (token === "Io") {
      return localize2.ordinalNumber(isoWeek, { unit: "week" });
    }
    return addLeadingZeros$1(isoWeek, token.length);
  },
  // Day of the month
  d: function(date, token, localize2) {
    if (token === "do") {
      return localize2.ordinalNumber(date.getDate(), { unit: "date" });
    }
    return lightFormatters.d(date, token);
  },
  // Day of year
  D: function(date, token, localize2) {
    const dayOfYear = getDayOfYear(date);
    if (token === "Do") {
      return localize2.ordinalNumber(dayOfYear, { unit: "dayOfYear" });
    }
    return addLeadingZeros$1(dayOfYear, token.length);
  },
  // Day of week
  E: function(date, token, localize2) {
    const dayOfWeek = date.getDay();
    switch (token) {
      // Tue
      case "E":
      case "EE":
      case "EEE":
        return localize2.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting"
        });
      // T
      case "EEEEE":
        return localize2.day(dayOfWeek, {
          width: "narrow",
          context: "formatting"
        });
      // Tu
      case "EEEEEE":
        return localize2.day(dayOfWeek, {
          width: "short",
          context: "formatting"
        });
      // Tuesday
      case "EEEE":
      default:
        return localize2.day(dayOfWeek, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // Local day of week
  e: function(date, token, localize2, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case "e":
        return String(localDayOfWeek);
      // Padded numerical value
      case "ee":
        return addLeadingZeros$1(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th
      case "eo":
        return localize2.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "eee":
        return localize2.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting"
        });
      // T
      case "eeeee":
        return localize2.day(dayOfWeek, {
          width: "narrow",
          context: "formatting"
        });
      // Tu
      case "eeeeee":
        return localize2.day(dayOfWeek, {
          width: "short",
          context: "formatting"
        });
      // Tuesday
      case "eeee":
      default:
        return localize2.day(dayOfWeek, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // Stand-alone local day of week
  c: function(date, token, localize2, options) {
    const dayOfWeek = date.getDay();
    const localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;
    switch (token) {
      // Numerical value (same as in `e`)
      case "c":
        return String(localDayOfWeek);
      // Padded numerical value
      case "cc":
        return addLeadingZeros$1(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th
      case "co":
        return localize2.ordinalNumber(localDayOfWeek, { unit: "day" });
      case "ccc":
        return localize2.day(dayOfWeek, {
          width: "abbreviated",
          context: "standalone"
        });
      // T
      case "ccccc":
        return localize2.day(dayOfWeek, {
          width: "narrow",
          context: "standalone"
        });
      // Tu
      case "cccccc":
        return localize2.day(dayOfWeek, {
          width: "short",
          context: "standalone"
        });
      // Tuesday
      case "cccc":
      default:
        return localize2.day(dayOfWeek, {
          width: "wide",
          context: "standalone"
        });
    }
  },
  // ISO day of week
  i: function(date, token, localize2) {
    const dayOfWeek = date.getDay();
    const isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    switch (token) {
      // 2
      case "i":
        return String(isoDayOfWeek);
      // 02
      case "ii":
        return addLeadingZeros$1(isoDayOfWeek, token.length);
      // 2nd
      case "io":
        return localize2.ordinalNumber(isoDayOfWeek, { unit: "day" });
      // Tue
      case "iii":
        return localize2.day(dayOfWeek, {
          width: "abbreviated",
          context: "formatting"
        });
      // T
      case "iiiii":
        return localize2.day(dayOfWeek, {
          width: "narrow",
          context: "formatting"
        });
      // Tu
      case "iiiiii":
        return localize2.day(dayOfWeek, {
          width: "short",
          context: "formatting"
        });
      // Tuesday
      case "iiii":
      default:
        return localize2.day(dayOfWeek, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // AM or PM
  a: function(date, token, localize2) {
    const hours = date.getHours();
    const dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
    switch (token) {
      case "a":
      case "aa":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting"
        });
      case "aaa":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting"
        }).toLowerCase();
      case "aaaaa":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting"
        });
      case "aaaa":
      default:
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // AM, PM, midnight, noon
  b: function(date, token, localize2) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? "pm" : "am";
    }
    switch (token) {
      case "b":
      case "bb":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting"
        });
      case "bbb":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting"
        }).toLowerCase();
      case "bbbbb":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting"
        });
      case "bbbb":
      default:
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function(date, token, localize2) {
    const hours = date.getHours();
    let dayPeriodEnumValue;
    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }
    switch (token) {
      case "B":
      case "BB":
      case "BBB":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "abbreviated",
          context: "formatting"
        });
      case "BBBBB":
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "narrow",
          context: "formatting"
        });
      case "BBBB":
      default:
        return localize2.dayPeriod(dayPeriodEnumValue, {
          width: "wide",
          context: "formatting"
        });
    }
  },
  // Hour [1-12]
  h: function(date, token, localize2) {
    if (token === "ho") {
      let hours = date.getHours() % 12;
      if (hours === 0) hours = 12;
      return localize2.ordinalNumber(hours, { unit: "hour" });
    }
    return lightFormatters.h(date, token);
  },
  // Hour [0-23]
  H: function(date, token, localize2) {
    if (token === "Ho") {
      return localize2.ordinalNumber(date.getHours(), { unit: "hour" });
    }
    return lightFormatters.H(date, token);
  },
  // Hour [0-11]
  K: function(date, token, localize2) {
    const hours = date.getHours() % 12;
    if (token === "Ko") {
      return localize2.ordinalNumber(hours, { unit: "hour" });
    }
    return addLeadingZeros$1(hours, token.length);
  },
  // Hour [1-24]
  k: function(date, token, localize2) {
    let hours = date.getHours();
    if (hours === 0) hours = 24;
    if (token === "ko") {
      return localize2.ordinalNumber(hours, { unit: "hour" });
    }
    return addLeadingZeros$1(hours, token.length);
  },
  // Minute
  m: function(date, token, localize2) {
    if (token === "mo") {
      return localize2.ordinalNumber(date.getMinutes(), { unit: "minute" });
    }
    return lightFormatters.m(date, token);
  },
  // Second
  s: function(date, token, localize2) {
    if (token === "so") {
      return localize2.ordinalNumber(date.getSeconds(), { unit: "second" });
    }
    return lightFormatters.s(date, token);
  },
  // Fraction of second
  S: function(date, token) {
    return lightFormatters.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function(date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();
    if (timezoneOffset === 0) {
      return "Z";
    }
    switch (token) {
      // Hours and optional minutes
      case "X":
        return formatTimezoneWithOptionalMinutes$1(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case "XXXX":
      case "XX":
        return formatTimezone$1(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case "XXXXX":
      case "XXX":
      // Hours and minutes with `:` delimiter
      default:
        return formatTimezone$1(timezoneOffset, ":");
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function(date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();
    switch (token) {
      // Hours and optional minutes
      case "x":
        return formatTimezoneWithOptionalMinutes$1(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case "xxxx":
      case "xx":
        return formatTimezone$1(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case "xxxxx":
      case "xxx":
      // Hours and minutes with `:` delimiter
      default:
        return formatTimezone$1(timezoneOffset, ":");
    }
  },
  // Timezone (GMT)
  O: function(date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();
    switch (token) {
      // Short
      case "O":
      case "OO":
      case "OOO":
        return "GMT" + formatTimezoneShort$1(timezoneOffset, ":");
      // Long
      case "OOOO":
      default:
        return "GMT" + formatTimezone$1(timezoneOffset, ":");
    }
  },
  // Timezone (specific non-location)
  z: function(date, token, _localize) {
    const timezoneOffset = date.getTimezoneOffset();
    switch (token) {
      // Short
      case "z":
      case "zz":
      case "zzz":
        return "GMT" + formatTimezoneShort$1(timezoneOffset, ":");
      // Long
      case "zzzz":
      default:
        return "GMT" + formatTimezone$1(timezoneOffset, ":");
    }
  },
  // Seconds timestamp
  t: function(date, token, _localize) {
    const timestamp = Math.trunc(+date / 1e3);
    return addLeadingZeros$1(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function(date, token, _localize) {
    return addLeadingZeros$1(+date, token.length);
  }
};
function formatTimezoneShort$1(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = Math.trunc(absOffset / 60);
  const minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  return sign + String(hours) + delimiter + addLeadingZeros$1(minutes, 2);
}
function formatTimezoneWithOptionalMinutes$1(offset, delimiter) {
  if (offset % 60 === 0) {
    const sign = offset > 0 ? "-" : "+";
    return sign + addLeadingZeros$1(Math.abs(offset) / 60, 2);
  }
  return formatTimezone$1(offset, delimiter);
}
function formatTimezone$1(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = addLeadingZeros$1(Math.trunc(absOffset / 60), 2);
  const minutes = addLeadingZeros$1(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}
const dateLongFormatter = (pattern, formatLong2) => {
  switch (pattern) {
    case "P":
      return formatLong2.date({ width: "short" });
    case "PP":
      return formatLong2.date({ width: "medium" });
    case "PPP":
      return formatLong2.date({ width: "long" });
    case "PPPP":
    default:
      return formatLong2.date({ width: "full" });
  }
};
const timeLongFormatter = (pattern, formatLong2) => {
  switch (pattern) {
    case "p":
      return formatLong2.time({ width: "short" });
    case "pp":
      return formatLong2.time({ width: "medium" });
    case "ppp":
      return formatLong2.time({ width: "long" });
    case "pppp":
    default:
      return formatLong2.time({ width: "full" });
  }
};
const dateTimeLongFormatter = (pattern, formatLong2) => {
  const matchResult = pattern.match(/(P+)(p+)?/) || [];
  const datePattern = matchResult[1];
  const timePattern = matchResult[2];
  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong2);
  }
  let dateTimeFormat;
  switch (datePattern) {
    case "P":
      dateTimeFormat = formatLong2.dateTime({ width: "short" });
      break;
    case "PP":
      dateTimeFormat = formatLong2.dateTime({ width: "medium" });
      break;
    case "PPP":
      dateTimeFormat = formatLong2.dateTime({ width: "long" });
      break;
    case "PPPP":
    default:
      dateTimeFormat = formatLong2.dateTime({ width: "full" });
      break;
  }
  return dateTimeFormat.replace("{{date}}", dateLongFormatter(datePattern, formatLong2)).replace("{{time}}", timeLongFormatter(timePattern, formatLong2));
};
const longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};
const dayOfYearTokenRE = /^D+$/;
const weekYearTokenRE = /^Y+$/;
const throwTokens = ["D", "DD", "YY", "YYYY"];
function isProtectedDayOfYearToken(token) {
  return dayOfYearTokenRE.test(token);
}
function isProtectedWeekYearToken(token) {
  return weekYearTokenRE.test(token);
}
function warnOrThrowProtectedError(token, format2, input) {
  const _message = message(token, format2, input);
  console.warn(_message);
  if (throwTokens.includes(token)) throw new RangeError(_message);
}
function message(token, format2, input) {
  const subject = token[0] === "Y" ? "years" : "days of the month";
  return `Use \`${token.toLowerCase()}\` instead of \`${token}\` (in \`${format2}\`) for formatting ${subject} to the input \`${input}\`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md`;
}
function isDate$1(value) {
  return value instanceof Date || typeof value === "object" && Object.prototype.toString.call(value) === "[object Date]";
}
function isValid(date) {
  return !(!isDate$1(date) && typeof date !== "number" || isNaN(+toDate$1(date)));
}
const formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g;
const longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
const escapedStringRegExp = /^'([^]*?)'?$/;
const doubleQuoteRegExp = /''/g;
const unescapedLatinCharacterRegExp = /[a-zA-Z]/;
function format$1(date, formatStr, options) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const defaultOptions2 = getDefaultOptions$1();
  const locale = (options == null ? void 0 : options.locale) ?? defaultOptions2.locale ?? enUS;
  const firstWeekContainsDate = (options == null ? void 0 : options.firstWeekContainsDate) ?? ((_b = (_a = options == null ? void 0 : options.locale) == null ? void 0 : _a.options) == null ? void 0 : _b.firstWeekContainsDate) ?? defaultOptions2.firstWeekContainsDate ?? ((_d = (_c = defaultOptions2.locale) == null ? void 0 : _c.options) == null ? void 0 : _d.firstWeekContainsDate) ?? 1;
  const weekStartsOn = (options == null ? void 0 : options.weekStartsOn) ?? ((_f = (_e = options == null ? void 0 : options.locale) == null ? void 0 : _e.options) == null ? void 0 : _f.weekStartsOn) ?? defaultOptions2.weekStartsOn ?? ((_h = (_g = defaultOptions2.locale) == null ? void 0 : _g.options) == null ? void 0 : _h.weekStartsOn) ?? 0;
  const originalDate = toDate$1(date, options == null ? void 0 : options.in);
  if (!isValid(originalDate)) {
    throw new RangeError("Invalid time value");
  }
  let parts = formatStr.match(longFormattingTokensRegExp).map((substring) => {
    const firstCharacter = substring[0];
    if (firstCharacter === "p" || firstCharacter === "P") {
      const longFormatter = longFormatters[firstCharacter];
      return longFormatter(substring, locale.formatLong);
    }
    return substring;
  }).join("").match(formattingTokensRegExp).map((substring) => {
    if (substring === "''") {
      return { isToken: false, value: "'" };
    }
    const firstCharacter = substring[0];
    if (firstCharacter === "'") {
      return { isToken: false, value: cleanEscapedString(substring) };
    }
    if (formatters$1[firstCharacter]) {
      return { isToken: true, value: substring };
    }
    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError(
        "Format string contains an unescaped latin alphabet character `" + firstCharacter + "`"
      );
    }
    return { isToken: false, value: substring };
  });
  if (locale.localize.preprocessor) {
    parts = locale.localize.preprocessor(originalDate, parts);
  }
  const formatterOptions = {
    firstWeekContainsDate,
    weekStartsOn,
    locale
  };
  return parts.map((part) => {
    if (!part.isToken) return part.value;
    const token = part.value;
    if (!(options == null ? void 0 : options.useAdditionalWeekYearTokens) && isProtectedWeekYearToken(token) || !(options == null ? void 0 : options.useAdditionalDayOfYearTokens) && isProtectedDayOfYearToken(token)) {
      warnOrThrowProtectedError(token, formatStr, String(date));
    }
    const formatter = formatters$1[token[0]];
    return formatter(originalDate, token, locale.localize, formatterOptions);
  }).join("");
}
function cleanEscapedString(input) {
  const matched = input.match(escapedStringRegExp);
  if (!matched) {
    return input;
  }
  return matched[1].replace(doubleQuoteRegExp, "'");
}
function getDefaultOptions() {
  return Object.assign({}, getDefaultOptions$1());
}
function tzIntlTimeZoneName(length, date, options) {
  const defaultOptions2 = getDefaultOptions();
  const dtf = getDTF(length, options.timeZone, options.locale ?? defaultOptions2.locale);
  return "formatToParts" in dtf ? partsTimeZone(dtf, date) : hackyTimeZone(dtf, date);
}
function partsTimeZone(dtf, date) {
  const formatted = dtf.formatToParts(date);
  for (let i = formatted.length - 1; i >= 0; --i) {
    if (formatted[i].type === "timeZoneName") {
      return formatted[i].value;
    }
  }
  return void 0;
}
function hackyTimeZone(dtf, date) {
  const formatted = dtf.format(date).replace(/\u200E/g, "");
  const tzNameMatch = / [\w-+ ]+$/.exec(formatted);
  return tzNameMatch ? tzNameMatch[0].substr(1) : "";
}
function getDTF(length, timeZone, locale) {
  return new Intl.DateTimeFormat(locale ? [locale.code, "en-US"] : void 0, {
    timeZone,
    timeZoneName: length
  });
}
function tzTokenizeDate(date, timeZone) {
  const dtf = getDateTimeFormat(timeZone);
  return "formatToParts" in dtf ? partsOffset(dtf, date) : hackyOffset(dtf, date);
}
const typeToPos = {
  year: 0,
  month: 1,
  day: 2,
  hour: 3,
  minute: 4,
  second: 5
};
function partsOffset(dtf, date) {
  try {
    const formatted = dtf.formatToParts(date);
    const filled = [];
    for (let i = 0; i < formatted.length; i++) {
      const pos = typeToPos[formatted[i].type];
      if (pos !== void 0) {
        filled[pos] = parseInt(formatted[i].value, 10);
      }
    }
    return filled;
  } catch (error) {
    if (error instanceof RangeError) {
      return [NaN];
    }
    throw error;
  }
}
function hackyOffset(dtf, date) {
  const formatted = dtf.format(date);
  const parsed = /(\d+)\/(\d+)\/(\d+),? (\d+):(\d+):(\d+)/.exec(formatted);
  return [
    parseInt(parsed[3], 10),
    parseInt(parsed[1], 10),
    parseInt(parsed[2], 10),
    parseInt(parsed[4], 10),
    parseInt(parsed[5], 10),
    parseInt(parsed[6], 10)
  ];
}
const dtfCache = {};
const testDateFormatted = new Intl.DateTimeFormat("en-US", {
  hourCycle: "h23",
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
}).format(/* @__PURE__ */ new Date("2014-06-25T04:00:00.123Z"));
const hourCycleSupported = testDateFormatted === "06/25/2014, 00:00:00" || testDateFormatted === "‎06‎/‎25‎/‎2014‎ ‎00‎:‎00‎:‎00";
function getDateTimeFormat(timeZone) {
  if (!dtfCache[timeZone]) {
    dtfCache[timeZone] = hourCycleSupported ? new Intl.DateTimeFormat("en-US", {
      hourCycle: "h23",
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    }) : new Intl.DateTimeFormat("en-US", {
      hour12: false,
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit"
    });
  }
  return dtfCache[timeZone];
}
function newDateUTC(fullYear, month, day, hour, minute, second, millisecond) {
  const utcDate = /* @__PURE__ */ new Date(0);
  utcDate.setUTCFullYear(fullYear, month, day);
  utcDate.setUTCHours(hour, minute, second, millisecond);
  return utcDate;
}
const MILLISECONDS_IN_HOUR$1 = 36e5;
const MILLISECONDS_IN_MINUTE$2 = 6e4;
const patterns$1 = {
  timezoneZ: /^(Z)$/,
  timezoneHH: /^([+-]\d{2})$/,
  timezoneHHMM: /^([+-])(\d{2}):?(\d{2})$/
};
function tzParseTimezone(timezoneString, date, isUtcDate) {
  if (!timezoneString) {
    return 0;
  }
  let token = patterns$1.timezoneZ.exec(timezoneString);
  if (token) {
    return 0;
  }
  let hours;
  let absoluteOffset;
  token = patterns$1.timezoneHH.exec(timezoneString);
  if (token) {
    hours = parseInt(token[1], 10);
    if (!validateTimezone(hours)) {
      return NaN;
    }
    return -(hours * MILLISECONDS_IN_HOUR$1);
  }
  token = patterns$1.timezoneHHMM.exec(timezoneString);
  if (token) {
    hours = parseInt(token[2], 10);
    const minutes = parseInt(token[3], 10);
    if (!validateTimezone(hours, minutes)) {
      return NaN;
    }
    absoluteOffset = Math.abs(hours) * MILLISECONDS_IN_HOUR$1 + minutes * MILLISECONDS_IN_MINUTE$2;
    return token[1] === "+" ? -absoluteOffset : absoluteOffset;
  }
  if (isValidTimezoneIANAString(timezoneString)) {
    date = new Date(date || Date.now());
    const utcDate = isUtcDate ? date : toUtcDate(date);
    const offset = calcOffset(utcDate, timezoneString);
    const fixedOffset = isUtcDate ? offset : fixOffset(date, offset, timezoneString);
    return -fixedOffset;
  }
  return NaN;
}
function toUtcDate(date) {
  return newDateUTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds());
}
function calcOffset(date, timezoneString) {
  const tokens = tzTokenizeDate(date, timezoneString);
  const asUTC = newDateUTC(tokens[0], tokens[1] - 1, tokens[2], tokens[3] % 24, tokens[4], tokens[5], 0).getTime();
  let asTS = date.getTime();
  const over = asTS % 1e3;
  asTS -= over >= 0 ? over : 1e3 + over;
  return asUTC - asTS;
}
function fixOffset(date, offset, timezoneString) {
  const localTS = date.getTime();
  let utcGuess = localTS - offset;
  const o2 = calcOffset(new Date(utcGuess), timezoneString);
  if (offset === o2) {
    return offset;
  }
  utcGuess -= o2 - offset;
  const o3 = calcOffset(new Date(utcGuess), timezoneString);
  if (o2 === o3) {
    return o2;
  }
  return Math.max(o2, o3);
}
function validateTimezone(hours, minutes) {
  return -23 <= hours && hours <= 23 && (minutes == null || 0 <= minutes && minutes <= 59);
}
const validIANATimezoneCache = {};
function isValidTimezoneIANAString(timeZoneString) {
  if (validIANATimezoneCache[timeZoneString])
    return true;
  try {
    new Intl.DateTimeFormat(void 0, { timeZone: timeZoneString });
    validIANATimezoneCache[timeZoneString] = true;
    return true;
  } catch (error) {
    return false;
  }
}
const MILLISECONDS_IN_MINUTE$1 = 60 * 1e3;
const formatters = {
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function(date, token, options) {
    const timezoneOffset = getTimeZoneOffset(options.timeZone, date);
    if (timezoneOffset === 0) {
      return "Z";
    }
    switch (token) {
      // Hours and optional minutes
      case "X":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimeter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`
      case "XXXX":
      case "XX":
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimeter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`
      case "XXXXX":
      case "XXX":
      // Hours and minutes with `:` delimeter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function(date, token, options) {
    const timezoneOffset = getTimeZoneOffset(options.timeZone, date);
    switch (token) {
      // Hours and optional minutes
      case "x":
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimeter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`
      case "xxxx":
      case "xx":
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimeter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`
      case "xxxxx":
      case "xxx":
      // Hours and minutes with `:` delimeter
      default:
        return formatTimezone(timezoneOffset, ":");
    }
  },
  // Timezone (GMT)
  O: function(date, token, options) {
    const timezoneOffset = getTimeZoneOffset(options.timeZone, date);
    switch (token) {
      // Short
      case "O":
      case "OO":
      case "OOO":
        return "GMT" + formatTimezoneShort(timezoneOffset, ":");
      // Long
      case "OOOO":
      default:
        return "GMT" + formatTimezone(timezoneOffset, ":");
    }
  },
  // Timezone (specific non-location)
  z: function(date, token, options) {
    switch (token) {
      // Short
      case "z":
      case "zz":
      case "zzz":
        return tzIntlTimeZoneName("short", date, options);
      // Long
      case "zzzz":
      default:
        return tzIntlTimeZoneName("long", date, options);
    }
  }
};
function getTimeZoneOffset(timeZone, originalDate) {
  const timeZoneOffset = timeZone ? tzParseTimezone(timeZone, originalDate, true) / MILLISECONDS_IN_MINUTE$1 : (originalDate == null ? void 0 : originalDate.getTimezoneOffset()) ?? 0;
  if (Number.isNaN(timeZoneOffset)) {
    throw new RangeError("Invalid time zone specified: " + timeZone);
  }
  return timeZoneOffset;
}
function addLeadingZeros(number, targetLength) {
  const sign = number < 0 ? "-" : "";
  let output = Math.abs(number).toString();
  while (output.length < targetLength) {
    output = "0" + output;
  }
  return sign + output;
}
function formatTimezone(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  const minutes = addLeadingZeros(Math.floor(absOffset % 60), 2);
  return sign + hours + delimiter + minutes;
}
function formatTimezoneWithOptionalMinutes(offset, delimiter) {
  if (offset % 60 === 0) {
    const sign = offset > 0 ? "-" : "+";
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }
  return formatTimezone(offset, delimiter);
}
function formatTimezoneShort(offset, delimiter = "") {
  const sign = offset > 0 ? "-" : "+";
  const absOffset = Math.abs(offset);
  const hours = Math.floor(absOffset / 60);
  const minutes = absOffset % 60;
  if (minutes === 0) {
    return sign + String(hours);
  }
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}
function getTimezoneOffsetInMilliseconds(date) {
  const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return +date - +utcDate;
}
const tzPattern = /(Z|[+-]\d{2}(?::?\d{2})?| UTC| [a-zA-Z]+\/[a-zA-Z_]+(?:\/[a-zA-Z_]+)?)$/;
const MILLISECONDS_IN_HOUR = 36e5;
const MILLISECONDS_IN_MINUTE = 6e4;
const DEFAULT_ADDITIONAL_DIGITS = 2;
const patterns = {
  dateTimePattern: /^([0-9W+-]+)(T| )(.*)/,
  datePattern: /^([0-9W+-]+)(.*)/,
  // year tokens
  YY: /^(\d{2})$/,
  YYY: [
    /^([+-]\d{2})$/,
    // 0 additional digits
    /^([+-]\d{3})$/,
    // 1 additional digit
    /^([+-]\d{4})$/
    // 2 additional digits
  ],
  YYYY: /^(\d{4})/,
  YYYYY: [
    /^([+-]\d{4})/,
    // 0 additional digits
    /^([+-]\d{5})/,
    // 1 additional digit
    /^([+-]\d{6})/
    // 2 additional digits
  ],
  // date tokens
  MM: /^-(\d{2})$/,
  DDD: /^-?(\d{3})$/,
  MMDD: /^-?(\d{2})-?(\d{2})$/,
  Www: /^-?W(\d{2})$/,
  WwwD: /^-?W(\d{2})-?(\d{1})$/,
  HH: /^(\d{2}([.,]\d*)?)$/,
  HHMM: /^(\d{2}):?(\d{2}([.,]\d*)?)$/,
  HHMMSS: /^(\d{2}):?(\d{2}):?(\d{2}([.,]\d*)?)$/,
  // time zone tokens (to identify the presence of a tz)
  timeZone: tzPattern
};
function toDate(argument, options = {}) {
  if (arguments.length < 1) {
    throw new TypeError("1 argument required, but only " + arguments.length + " present");
  }
  if (argument === null) {
    return /* @__PURE__ */ new Date(NaN);
  }
  const additionalDigits = options.additionalDigits == null ? DEFAULT_ADDITIONAL_DIGITS : Number(options.additionalDigits);
  if (additionalDigits !== 2 && additionalDigits !== 1 && additionalDigits !== 0) {
    throw new RangeError("additionalDigits must be 0, 1 or 2");
  }
  if (argument instanceof Date || typeof argument === "object" && Object.prototype.toString.call(argument) === "[object Date]") {
    return new Date(argument.getTime());
  } else if (typeof argument === "number" || Object.prototype.toString.call(argument) === "[object Number]") {
    return new Date(argument);
  } else if (!(Object.prototype.toString.call(argument) === "[object String]")) {
    return /* @__PURE__ */ new Date(NaN);
  }
  const dateStrings = splitDateString(argument);
  const { year, restDateString } = parseYear(dateStrings.date, additionalDigits);
  const date = parseDate(restDateString, year);
  if (date === null || isNaN(date.getTime())) {
    return /* @__PURE__ */ new Date(NaN);
  }
  if (date) {
    const timestamp = date.getTime();
    let time = 0;
    let offset;
    if (dateStrings.time) {
      time = parseTime(dateStrings.time);
      if (time === null || isNaN(time)) {
        return /* @__PURE__ */ new Date(NaN);
      }
    }
    if (dateStrings.timeZone || options.timeZone) {
      offset = tzParseTimezone(dateStrings.timeZone || options.timeZone, new Date(timestamp + time));
      if (isNaN(offset)) {
        return /* @__PURE__ */ new Date(NaN);
      }
    } else {
      offset = getTimezoneOffsetInMilliseconds(new Date(timestamp + time));
      offset = getTimezoneOffsetInMilliseconds(new Date(timestamp + time + offset));
    }
    return new Date(timestamp + time + offset);
  } else {
    return /* @__PURE__ */ new Date(NaN);
  }
}
function splitDateString(dateString) {
  const dateStrings = {};
  let parts = patterns.dateTimePattern.exec(dateString);
  let timeString;
  if (!parts) {
    parts = patterns.datePattern.exec(dateString);
    if (parts) {
      dateStrings.date = parts[1];
      timeString = parts[2];
    } else {
      dateStrings.date = null;
      timeString = dateString;
    }
  } else {
    dateStrings.date = parts[1];
    timeString = parts[3];
  }
  if (timeString) {
    const token = patterns.timeZone.exec(timeString);
    if (token) {
      dateStrings.time = timeString.replace(token[1], "");
      dateStrings.timeZone = token[1].trim();
    } else {
      dateStrings.time = timeString;
    }
  }
  return dateStrings;
}
function parseYear(dateString, additionalDigits) {
  if (dateString) {
    const patternYYY = patterns.YYY[additionalDigits];
    const patternYYYYY = patterns.YYYYY[additionalDigits];
    let token = patterns.YYYY.exec(dateString) || patternYYYYY.exec(dateString);
    if (token) {
      const yearString = token[1];
      return {
        year: parseInt(yearString, 10),
        restDateString: dateString.slice(yearString.length)
      };
    }
    token = patterns.YY.exec(dateString) || patternYYY.exec(dateString);
    if (token) {
      const centuryString = token[1];
      return {
        year: parseInt(centuryString, 10) * 100,
        restDateString: dateString.slice(centuryString.length)
      };
    }
  }
  return {
    year: null
  };
}
function parseDate(dateString, year) {
  if (year === null) {
    return null;
  }
  let date;
  let month;
  let week;
  if (!dateString || !dateString.length) {
    date = /* @__PURE__ */ new Date(0);
    date.setUTCFullYear(year);
    return date;
  }
  let token = patterns.MM.exec(dateString);
  if (token) {
    date = /* @__PURE__ */ new Date(0);
    month = parseInt(token[1], 10) - 1;
    if (!validateDate(year, month)) {
      return /* @__PURE__ */ new Date(NaN);
    }
    date.setUTCFullYear(year, month);
    return date;
  }
  token = patterns.DDD.exec(dateString);
  if (token) {
    date = /* @__PURE__ */ new Date(0);
    const dayOfYear = parseInt(token[1], 10);
    if (!validateDayOfYearDate(year, dayOfYear)) {
      return /* @__PURE__ */ new Date(NaN);
    }
    date.setUTCFullYear(year, 0, dayOfYear);
    return date;
  }
  token = patterns.MMDD.exec(dateString);
  if (token) {
    date = /* @__PURE__ */ new Date(0);
    month = parseInt(token[1], 10) - 1;
    const day = parseInt(token[2], 10);
    if (!validateDate(year, month, day)) {
      return /* @__PURE__ */ new Date(NaN);
    }
    date.setUTCFullYear(year, month, day);
    return date;
  }
  token = patterns.Www.exec(dateString);
  if (token) {
    week = parseInt(token[1], 10) - 1;
    if (!validateWeekDate(week)) {
      return /* @__PURE__ */ new Date(NaN);
    }
    return dayOfISOWeekYear(year, week);
  }
  token = patterns.WwwD.exec(dateString);
  if (token) {
    week = parseInt(token[1], 10) - 1;
    const dayOfWeek = parseInt(token[2], 10) - 1;
    if (!validateWeekDate(week, dayOfWeek)) {
      return /* @__PURE__ */ new Date(NaN);
    }
    return dayOfISOWeekYear(year, week, dayOfWeek);
  }
  return null;
}
function parseTime(timeString) {
  let hours;
  let minutes;
  let token = patterns.HH.exec(timeString);
  if (token) {
    hours = parseFloat(token[1].replace(",", "."));
    if (!validateTime(hours)) {
      return NaN;
    }
    return hours % 24 * MILLISECONDS_IN_HOUR;
  }
  token = patterns.HHMM.exec(timeString);
  if (token) {
    hours = parseInt(token[1], 10);
    minutes = parseFloat(token[2].replace(",", "."));
    if (!validateTime(hours, minutes)) {
      return NaN;
    }
    return hours % 24 * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE;
  }
  token = patterns.HHMMSS.exec(timeString);
  if (token) {
    hours = parseInt(token[1], 10);
    minutes = parseInt(token[2], 10);
    const seconds = parseFloat(token[3].replace(",", "."));
    if (!validateTime(hours, minutes, seconds)) {
      return NaN;
    }
    return hours % 24 * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE + seconds * 1e3;
  }
  return null;
}
function dayOfISOWeekYear(isoWeekYear, week, day) {
  week = week || 0;
  day = day || 0;
  const date = /* @__PURE__ */ new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  const fourthOfJanuaryDay = date.getUTCDay() || 7;
  const diff = week * 7 + day + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const DAYS_IN_MONTH_LEAP_YEAR = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0;
}
function validateDate(year, month, date) {
  if (month < 0 || month > 11) {
    return false;
  }
  if (date != null) {
    if (date < 1) {
      return false;
    }
    const isLeapYear = isLeapYearIndex(year);
    if (isLeapYear && date > DAYS_IN_MONTH_LEAP_YEAR[month]) {
      return false;
    }
    if (!isLeapYear && date > DAYS_IN_MONTH[month]) {
      return false;
    }
  }
  return true;
}
function validateDayOfYearDate(year, dayOfYear) {
  if (dayOfYear < 1) {
    return false;
  }
  const isLeapYear = isLeapYearIndex(year);
  if (isLeapYear && dayOfYear > 366) {
    return false;
  }
  if (!isLeapYear && dayOfYear > 365) {
    return false;
  }
  return true;
}
function validateWeekDate(week, day) {
  if (week < 0 || week > 52) {
    return false;
  }
  if (day != null && (day < 0 || day > 6)) {
    return false;
  }
  return true;
}
function validateTime(hours, minutes, seconds) {
  if (hours < 0 || hours >= 25) {
    return false;
  }
  if (minutes != null && (minutes < 0 || minutes >= 60)) {
    return false;
  }
  if (seconds != null && (seconds < 0 || seconds >= 60)) {
    return false;
  }
  return true;
}
const tzFormattingTokensRegExp = /([xXOz]+)|''|'(''|[^'])+('|$)/g;
function format(date, formatStr, options = {}) {
  formatStr = String(formatStr);
  const matches = formatStr.match(tzFormattingTokensRegExp);
  if (matches) {
    const d = toDate(options.originalDate || date, options);
    formatStr = matches.reduce(function(result, token) {
      if (token[0] === "'") {
        return result;
      }
      const pos = result.indexOf(token);
      const precededByQuotedSection = result[pos - 1] === "'";
      const replaced = result.replace(token, "'" + formatters[token[0]](d, token, options) + "'");
      return precededByQuotedSection ? replaced.substring(0, pos - 1) + replaced.substring(pos + 1) : replaced;
    }, formatStr);
  }
  return format$1(date, formatStr, options);
}
function toZonedTime(date, timeZone, options) {
  date = toDate(date, options);
  const offsetMilliseconds = tzParseTimezone(timeZone, date, true);
  const d = new Date(date.getTime() - offsetMilliseconds);
  const resultDate = /* @__PURE__ */ new Date(0);
  resultDate.setFullYear(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  resultDate.setHours(d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds());
  return resultDate;
}
function formatInTimeZone(date, timeZone, formatStr, options) {
  options = {
    ...options,
    timeZone,
    originalDate: date
  };
  return format(toZonedTime(date, timeZone, { timeZone: options.timeZone }), formatStr, options);
}
const formatDateISOWithoutTimezone = ({ date, hours = 0 }) => {
  if (date === void 0) {
    return void 0;
  }
  if (date === null) {
    return null;
  }
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd'T'HH:mm:ss").replace("T00", `T${String(hours).padStart(2, "0")}`);
};
const formatDateISOWithTimezone = ({
  dateString,
  hours = 0
}) => {
  if (dateString === void 0) {
    return void 0;
  }
  if (!isString(dateString)) {
    return null;
  }
  if (dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(.\d+)?Z$/) != null) {
    return dateString;
  }
  if (dateString.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(.\d+)?$/) != null) {
    return `${dateString}Z`;
  }
  if (dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/) != null) {
    return `${dateString}T${String(hours).padStart(2, "0")}:00:00Z`;
  }
  throw new Error("dateString format is not valid");
};
const parseBookingGuestExtensions = (responseData) => {
  const {
    price_total,
    price,
    fees,
    price_rental_cover,
    requests: {
      km_200,
      km_unlimited,
      day_before,
      day_after,
      day_slide
    },
    rules,
    ...rest
  } = responseData;
  return {
    ...rest,
    price_total: parseFloat(price_total),
    price: parseFloat(price),
    fees: parseFloat(fees),
    price_rental_cover: parseFloat(price_rental_cover),
    requests: {
      km_unlimited: km_unlimited ? {
        ...km_unlimited,
        price: parseFloat(km_unlimited.price),
        price_total: parseFloat(km_unlimited.price_total),
        fees: parseFloat(km_unlimited.fees)
      } : null,
      km_200: km_200 ? {
        ...km_200,
        price: parseFloat(km_200.price),
        price_total: parseFloat(km_200.price_total),
        fees: parseFloat(km_200.fees)
      } : null,
      day_before: day_before ? {
        ...day_before,
        date_time_from: formatDateISOWithoutTimezone({
          date: day_before.date_from,
          hours: day_before.hour_from
        }),
        price: parseFloat(day_before.price),
        price_total: parseFloat(day_before.price_total),
        fees: parseFloat(day_before.fees),
        price_rental_cover: parseFloat(day_before.price_rental_cover)
      } : null,
      day_after: day_after ? {
        ...day_after,
        date_time_to: formatDateISOWithoutTimezone({
          date: day_after.date_to,
          hours: day_after.hour_to
        }),
        price: parseFloat(day_after.price),
        price_total: parseFloat(day_after.price_total),
        fees: parseFloat(day_after.fees),
        price_rental_cover: parseFloat(day_after.price_rental_cover)
      } : null,
      day_slide: day_slide ? {
        ...day_slide,
        date_time_to: formatDateISOWithoutTimezone({
          date: day_slide.date_to,
          hours: day_slide.hour_to
        }),
        date_time_from: formatDateISOWithoutTimezone({
          date: day_slide.date_from,
          hours: day_slide.hour_from
        }),
        price: parseFloat(day_slide.price),
        price_total: parseFloat(day_slide.price_total),
        fees: parseFloat(day_slide.fees),
        price_rental_cover: parseFloat(day_slide.price_rental_cover)
      } : null
    },
    rules: {
      ...rules,
      earliest_date_time_from: formatDateISOWithoutTimezone({
        date: rules.earliest_date_from
      }),
      latest_date_time_to: formatDateISOWithoutTimezone({
        date: rules.latest_date_to
      })
    }
  };
};
const parseBookingGuestUpgrades = (responseData) => {
  const { price_total, price, fees, ...rest } = responseData;
  return {
    ...rest,
    price_total: parseFloat(price_total),
    price: parseFloat(price),
    fees: parseFloat(fees)
  };
};
const parseBookingGuest = (responseData) => {
  const {
    travelers,
    price,
    camper,
    owner,
    extensions,
    upgrades,
    deposit,
    ...rest
  } = responseData;
  return {
    ...rest,
    date_time_from: formatDateISOWithoutTimezone({ date: rest.date_from, hours: rest.hour_from }),
    date_time_to: formatDateISOWithoutTimezone({ date: rest.date_to, hours: rest.hour_to }),
    date_time_acceptation_expired: formatDateISOWithoutTimezone({ date: rest.acceptation_expired_on }),
    date_time_confirmed: formatDateISOWithoutTimezone({ date: rest.confirmed_on }),
    travelers: parseInt(travelers),
    price: parseFloat(price),
    deposit: parseFloat(deposit),
    camper: {
      ...camper,
      is_ambassador: !!camper.is_ambassador,
      review_average: parseFloat(camper.review_average),
      deposit: parseFloat(camper.deposit)
    },
    owner: {
      ...owner,
      date_time_joined_on: formatDateISOWithTimezone({ dateString: owner.joined_on }),
      answer_rate_as_owner: owner.answer_rate_as_owner ? parseFloat(owner.answer_rate_as_owner) : 0,
      review_average_as_owner: parseFloat(owner.review_average_as_owner)
    },
    extensions: parseBookingGuestExtensions(extensions),
    upgrades: parseBookingGuestUpgrades(upgrades)
  };
};
class RequestCreateBookingGuest extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/bookings/", baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestCreateBookingGuestExtensions extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/extensions/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestCreateBookingGuestPayment extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-requests/${this._payload.idBookingGuest}/payment/summary/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateBookingGuestPaymentAsync extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-requests/${this._payload.idBookingGuest}/payment/async/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateBookingGuestPaymentConfirmation extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-requests/${this._payload.idBookingGuest}/payment/confirm/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateBookingGuestUpgrades extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/upgrades/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
const parsePeriod = (period) => {
  period.date_from = formatDateISOWithoutTimezone({ date: period.date_from });
  period.date_to = formatDateISOWithoutTimezone({ date: period.date_to });
  return period;
};
class RequestCreateCamperPeriod extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/calendar/periods/`, baseURL);
  }
  formatPayload() {
    const { idCamper, dates_0, dates_1, ...rest } = this._payload;
    return {
      ...rest,
      dates_0: formatDateISOWithoutTime({ date: dates_0 }),
      dates_1: formatDateISOWithoutTime({ date: dates_1 })
    };
  }
  parseResult(responseData) {
    return parsePeriod(responseData);
  }
}
class RequestCreateCamperPrice extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/calendar/prices/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateCamperPricingOption extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/options/`, baseURL);
  }
  formatPayload() {
    const { idCamper, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateCamperReactivation extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/update/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
const parseChatMessage = (responseData) => {
  return {
    ...responseData,
    date_time_sent: formatDateISOWithoutTimezone({ date: responseData.send_on_tz })
  };
};
class RequestCreateChatMessage extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/chats/${this._payload.idChat}/messages/`, baseURL);
  }
  formatPayload() {
    const { idChat: _idChat, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseChatMessage(responseData);
  }
}
class RequestCreateDocumentPage extends YscApiRequestCreateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/docs/${this._payload.idDocument}/pages/`, baseURL);
  }
  formatPayload() {
    return this._payload.page;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateEarningsSimulation extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/campers/earnings-simulation/", baseURL);
  }
  parseResult(data) {
    return {
      ...data,
      min_daily: parseInt(data.min_daily),
      max_daily: parseInt(data.max_daily),
      min_yearly: parseInt(data.min_yearly),
      max_yearly: parseInt(data.max_yearly)
    };
  }
}
const parseIban = (responseData) => {
  const defaultValues = {
    id: DOCUMENT_TYPES.IBAN,
    document_type: DOCUMENT_TYPES.IBAN
  };
  if (responseData) {
    return {
      ...responseData,
      ...defaultValues,
      status: DOCUMENT_STATES.VALIDATED
    };
  }
  return {
    ...defaultValues,
    status: DOCUMENT_STATES.CREATED
  };
};
class RequestCreateIban extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/mangopay/iban/", baseURL);
  }
  parseResult(responseData) {
    return parseIban(responseData);
  }
}
const parseInsuranceConditions = (responseData) => {
  const {
    deductible_amount,
    deductible_upper_parts,
    deductible_burglary,
    deductible_glass_breakage,
    ...rest
  } = responseData;
  return {
    ...rest,
    deductible_amount: parseFloat(deductible_amount),
    deductible_upper_parts: parseFloat(deductible_upper_parts),
    deductible_burglary: parseFloat(deductible_burglary),
    deductible_glass_breakage: parseFloat(deductible_glass_breakage)
  };
};
class RequestCreateInsuranceConditions extends YscApiRequestCreateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/insurance/condition/product/${this._payload.idProduct}/`, baseURL);
  }
  formatPayload() {
    return this._payload.form;
  }
  parseResult(responseData) {
    return parseInsuranceConditions(responseData);
  }
}
class RequestCreateLogAsCredentials extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/users/auth/log-as/", baseURL);
  }
  formatPayload() {
    return { user_id: +this._payload.user_id };
  }
  parseResult(data) {
    return data;
  }
}
class RequestCreateLoginCredentials extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/oauth/token/", baseURL);
  }
  formatPayload() {
    const encoded = {
      ...this._payload,
      grant_type: "password"
    };
    return fromObjectToURLSearchParams(encoded);
  }
  parseResult(data) {
    return data;
  }
}
class RequestCreateLogout extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/oauth/revoke_token/", baseURL);
  }
  formatPayload() {
    return fromObjectToURLSearchParams(this._payload);
  }
  parseResult(data) {
    return data;
  }
}
class RequestCreateNavigationEvent extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/events/send/", baseURL);
  }
  parseResult(data) {
    return data;
  }
}
class RequestCreateNewsletterRegistration extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/contacts/", baseURL);
  }
  formatPayload() {
    return { email: this._payload.email, listIds: [Number.parseInt(this._payload.listIds)] };
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreatePasswordReset extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/manage-password/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreatePhoneCertification extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/certify-phone/", baseURL);
  }
  parseResult(data) {
    return data;
  }
}
class RequestCreateProfilePicture extends YscApiRequestCreateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/users/me/pictures/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateRefreshCredentials extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/oauth/token/", baseURL);
  }
  formatPayload() {
    const encoded = {
      ...this._payload,
      grant_type: "refresh_token"
    };
    return fromObjectToURLSearchParams(encoded);
  }
  parseResult(data) {
    return data;
  }
}
class RequestCreateReview extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/review/create/${this._payload.idBooking}/`, baseURL);
  }
  formatPayload() {
    const { idBooking, ...payload } = this._payload;
    return payload;
  }
  parseResult(responseData) {
    return responseData;
  }
}
const formatUBODeclarationUser = (payload) => {
  const { idUbo: _idUbo, birthday, ...p } = payload;
  return {
    ...p,
    birthday: formatDateISOWithoutTime({ date: birthday })
  };
};
const parseUBODeclarationUser = (user) => ({
  ...user,
  birthday: formatDateISOWithoutTimezone({ date: user.birthday })
});
class RequestCreateUBODeclarationUser extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/mangopay/ubo-declaration/${this._payload.idUbo}/ubo/`, baseURL);
  }
  formatPayload() {
    return formatUBODeclarationUser(this._payload);
  }
  parseResult(responseData) {
    return parseUBODeclarationUser(responseData);
  }
}
const parseUser = (user) => {
  if ("borned_on" in user) {
    user.borned_on = formatDateISOWithoutTimezone({ date: user.borned_on });
  }
  user.joined_at = formatDateISOWithoutTimezone({ date: user.joined_at });
  return user;
};
class RequestCreateUser extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/users/", baseURL);
  }
  parseResult(responseData) {
    return parseUser(responseData);
  }
}
class RequestCreateUserCompany extends YscApiRequestCreate {
  constructor({ payload } = {}) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/users/me/company/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestCreateUserLegalOwner extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  formatPayload() {
    const { borned_on: raw_borned_on, ...rest } = this._payload;
    let borned_on = void 0;
    if (raw_borned_on) {
      borned_on = formatDateISOWithoutTime({ date: raw_borned_on });
    }
    return {
      ...rest,
      borned_on
    };
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/users/me/legal/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
const formatDateISOWithoutTime = ({ date }) => {
  if (date === void 0) {
    return void 0;
  }
  if (date === null) {
    return null;
  }
  return formatInTimeZone(date, "UTC", "yyyy-MM-dd");
};
const parseVehicleEquipments = (responseData) => {
  const payload = Object.assign({}, responseData);
  const toTransform = ["cooking_energies", "heating_energies", "fridge_energies", "electric_plug_types"];
  toTransform.forEach((key) => {
    if (payload[key] === null) {
      payload[key] = [];
    }
  });
  return payload;
};
const parseVehicle = (responseData) => {
  const { date, equipments, ...rest } = responseData;
  return {
    ...rest,
    date: formatDateISOWithoutTime({ date }),
    equipments: parseVehicleEquipments(equipments)
  };
};
class RequestCreateVehicle extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  formatPayload() {
    const { date, ...rest } = this._payload;
    return {
      ...rest,
      date: formatDateISOWithoutTime({ date })
    };
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/vehicles/", baseURL);
  }
  parseResult(responseData) {
    return parseVehicle(responseData);
  }
}
class RequestCreateVehicleSleeping extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/sleepings/`, baseURL);
  }
  formatPayload() {
    const { idVehicle, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return responseData;
  }
}
const parseCamper = (camperItem) => {
  const { deposit_mean, vehicle_owner_language_spoke, published_at, abroad_allowed, smoking_allowed, pets_allowed, ...origin } = camperItem;
  const mapTrileanToAcceptanceState = (acceptanceStatus) => {
    const { ALLOWED, NOT_ALLOWED, ON_DEMAND } = OWNER_ACCEPTANCE_STATES;
    return acceptanceStatus ? ALLOWED : acceptanceStatus === false ? NOT_ALLOWED : ON_DEMAND;
  };
  return {
    ...origin,
    deposit_mean: stringToArray(deposit_mean),
    vehicle_owner_language_spoke: stringToArray(vehicle_owner_language_spoke),
    published_at: formatDateISOWithTimezone({ dateString: published_at }),
    abroad_allowed: mapTrileanToAcceptanceState(abroad_allowed),
    smoking_allowed: mapTrileanToAcceptanceState(smoking_allowed),
    pets_allowed: mapTrileanToAcceptanceState(pets_allowed)
  };
};
const parseWishlist = (responseData) => {
  const { ad_set, ...rest } = responseData;
  return {
    ...rest,
    ad_set: ad_set.map(parseCamper)
  };
};
class RequestCreateWishlist extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/wishlists/", baseURL);
  }
  parseResult(responseData) {
    return parseWishlist(responseData);
  }
}
class RequestCreateWishlistCamper extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/wishlist/${this._payload.slug}/product/`, baseURL);
  }
  formatPayload() {
    const { slug, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseWishlist(responseData);
  }
}
class RequestCreateHubspotPushLeadAdOnboarding extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/crm/hubspot/push-lead-ad-onboarding/${this._payload.idUser}/`, baseURL);
  }
  parseResult(data) {
    return data;
  }
}
class RequestDeleteBookingGuestExtensions extends YscApiRequestDelete {
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/extensions/${this._payload.idBookingGuestExtensions}/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestDeleteBookingGuestUpgrades extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/upgrades/${this._payload.idBookingGuestUpgrades}/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestDeleteCamper extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    const { idCamper, ...query } = this._payload;
    const url = new URL(`/3/products/${idCamper}/update/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteCamperPeriod extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/calendar/periods/${this._payload.idPeriod}/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteCamperPrice extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/calendar/prices/${this._payload.idPrice}/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteCamperPricingOption extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/options/`, baseURL);
  }
  formatPayload() {
    const { idOption: id } = this._payload;
    return { id };
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteDocumentPage extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/docs/${this._payload.idDocument}/pages/${this._payload.idPage}/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteUserMe extends YscApiRequestDelete {
  getEndpointURL(baseURL) {
    return new URL("/3/users/me/", baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteVehiclePhoto extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/photos/${this._payload.idPhoto}/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteVehicleSleeping extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/sleepings/${this._payload.idSleeping}/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteWishlist extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/wishlist/${this._payload.slug}/`, baseURL);
  }
  /* v8 ignore next 3 */
  parseResult() {
    return;
  }
}
class RequestDeleteWishlistCamper extends YscApiRequestDelete {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/wishlist/${this._payload.slug}/product/`, baseURL);
  }
  formatPayload() {
    const { slug, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseWishlist(responseData);
  }
}
class RequestReadBookingGuest extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._query.idBookingGuest}/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestReadBookingGuestCheckoutInfos extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-requests/${this._query.idBookingGuest}/payment/summary/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestExtensionsPaymentInfos extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._query.idBookingGuest}/extensions/confirm/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestPayment extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idBookingGuest, ...query } = this._query;
    const url = new URL(`/3/my-requests/${this._query.idBookingGuest}/payment/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestPaymentAsync extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-requests/${this._query.idBookingGuest}/payment/async/${this._query.idPayin}/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestPaymentFinalization extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idBookingGuest, ...query } = this._query;
    const url = new URL(`/3/my-requests/${idBookingGuest}/payment/final/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestPaymentMethods extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-requests/${this._query.idBookingGuest}/payments/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestPaymentRedirection extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idBookingGuest, ...query } = this._query;
    const url = new URL(`/3/my-requests/${idBookingGuest}/payment/web-redirect/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
const isString = (x) => Object.prototype.toString.call(x) === "[object String]";
const parseBookingPrices = (responseData) => {
  const toPriceObject = (value, currency) => {
    if (value === null) {
      return null;
    }
    return {
      value: isString(value) ? parseFloat(value) : value,
      currency
    };
  };
  const { currency_used, price_cancellation_insurance_per_day, price_cancellation_insurance_per_day_currency, currency_owner, price, price_cancellation_insurance, price_coupon, price_discount, price_discount_early, price_discount_weekly, price_fees, price_fees_ydm_owner, price_fees_owner_excluding_ydm_owner, price_guest_per_day, price_insurance, price_km, price_options, price_owner, price_second_driver, price_owner_per_day, price_raw, price_without_discount, price_rental_cover, price_rental_cover_per_day, price_2x_first, price_2x_second, second_payment_date, ...rest } = responseData;
  return {
    ...rest,
    currency_used,
    currency_owner,
    price: toPriceObject(price, currency_used),
    price_cancellation_insurance: toPriceObject(price_cancellation_insurance, currency_owner),
    price_coupon: toPriceObject(price_coupon, currency_owner),
    price_discount: toPriceObject(price_discount, currency_owner),
    price_discount_early: toPriceObject(price_discount_early, currency_owner),
    price_discount_weekly: toPriceObject(price_discount_weekly, currency_owner),
    price_fees: toPriceObject(price_fees, currency_owner),
    price_fees_owner_excluding_ydm_owner: toPriceObject(price_fees_owner_excluding_ydm_owner, currency_owner),
    price_fees_ydm_owner: toPriceObject(price_fees_ydm_owner, currency_owner),
    price_guest_per_day: toPriceObject(price_guest_per_day, currency_owner),
    price_insurance: toPriceObject(price_insurance, currency_owner),
    price_km: toPriceObject(price_km, currency_owner),
    price_options: toPriceObject(price_options, currency_owner),
    price_owner: toPriceObject(price_owner, currency_owner),
    price_second_driver: toPriceObject(price_second_driver, currency_owner),
    price_owner_per_day: toPriceObject(price_owner_per_day, currency_owner),
    price_raw: toPriceObject(price_raw, currency_owner),
    price_without_discount: toPriceObject(price_without_discount, currency_owner),
    price_rental_cover: toPriceObject(price_rental_cover, currency_owner),
    price_rental_cover_per_day: toPriceObject(price_rental_cover_per_day, currency_owner),
    price_cancellation_insurance_per_day: toPriceObject(price_cancellation_insurance_per_day, price_cancellation_insurance_per_day_currency),
    price_2x_first: toPriceObject(price_2x_first, currency_owner),
    price_2x_second: toPriceObject(price_2x_second, currency_owner),
    second_payment_date: formatDateISOWithoutTimezone({ date: second_payment_date })
  };
};
class RequestReadBookingGuestPrices extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idBookingGuest, ...query } = this._query;
    const url = new URL(`/3/my-bookings/${idBookingGuest}/prices/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return parseBookingPrices(responseData);
  }
}
class RequestReadBookingGuestRefund extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-bookings/${this._query.idBookingGuest}/refund/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingGuestUpgrades extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idBookingGuest, ...query } = this._query;
    const url = new URL(`/v4/bookings-guest/${idBookingGuest}/upgrades/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return parseBookingGuestUpgrades(responseData);
  }
}
const parseBookingOwnerExtensions = (responseData) => {
  const {
    earnings,
    requests: {
      km_200,
      km_unlimited,
      day_before,
      day_after,
      day_slide
    },
    ...rest
  } = responseData;
  return {
    ...rest,
    earnings: parseFloat(earnings),
    requests: {
      day_after: day_after ? {
        ...day_after,
        date_time_to: formatDateISOWithoutTimezone({ date: day_after.date_to, hours: day_after.hour_to }),
        earnings: parseFloat(day_after.earnings)
      } : null,
      day_before: day_before ? {
        ...day_before,
        date_time_from: formatDateISOWithoutTimezone({ date: day_before.date_from, hours: day_before.hour_from }),
        earnings: parseFloat(day_before.earnings)
      } : null,
      km_200: km_200 ? {
        ...km_200,
        earnings: parseFloat(km_200.earnings)
      } : null,
      km_unlimited: km_unlimited ? {
        ...km_unlimited,
        earnings: parseFloat(km_unlimited.earnings)
      } : null,
      day_slide: day_slide ? {
        ...day_slide,
        date_time_from: formatDateISOWithoutTimezone({ date: day_slide.date_from, hours: day_slide.hour_from }),
        date_time_to: formatDateISOWithoutTimezone({ date: day_slide.date_to, hours: day_slide.hour_to }),
        earnings: parseFloat(day_slide.earnings)
      } : null
    }
  };
};
const parseBookingOwner = (responseData) => {
  const { travelers, total_earnings, guest, camper, extensions, ...rest } = responseData;
  return {
    ...rest,
    date_time_from: formatDateISOWithoutTimezone({ date: rest.date_from, hours: rest.hour_from }),
    date_time_to: formatDateISOWithoutTimezone({ date: rest.date_to, hours: rest.hour_to }),
    date_time_confirmed: formatDateISOWithTimezone({ dateString: rest.confirmed_on }),
    owner_accept_expire_on: formatDateISOWithTimezone({ dateString: rest.owner_accept_expire_on }),
    travelers: travelers ? parseInt(travelers) : null,
    total_earnings: parseFloat(total_earnings),
    deposit: parseFloat(responseData.deposit),
    guest: {
      ...guest,
      review_average_as_guest: parseFloat(guest.review_average_as_guest)
    },
    camper: {
      ...camper,
      deposit: parseFloat(camper.deposit),
      review_average: parseFloat(camper.review_average)
    },
    extensions: parseBookingOwnerExtensions(extensions)
  };
};
class RequestReadBookingOwner extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-owner/${this._query.idBookingOwner}/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingOwner(responseData);
  }
}
class RequestReadBookingOwnerPrices extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/my-bookings/${this._query.idBookingOwner}/prices/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingPrices(responseData);
  }
}
class RequestReadBookingRequestPrice extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idCamper, ...query } = this._query;
    const url = new URL(`/3/products/${idCamper}/price/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingsGuest extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/bookings-guest/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    const { results, ...rest } = responseData;
    return {
      ...rest,
      results: results.map((r) => ({
        ...r,
        date_time_from: formatDateISOWithoutTimezone({ date: r.date_from, hours: r.hour_from }),
        date_time_to: formatDateISOWithoutTimezone({ date: r.date_to, hours: r.hour_to }),
        price: parseFloat(r.price),
        camper: {
          ...r.camper,
          review_average: parseFloat(r.camper.review_average)
        }
      }))
    };
  }
}
class RequestReadBookingsGuestFilters extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/v4/bookings-guest/options/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBookingsOwner extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/bookings-owner/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    const { results, ...rest } = responseData;
    return {
      ...rest,
      results: results.map((r) => ({
        ...r,
        date_time_from: formatDateISOWithoutTimezone({ date: r.date_from, hours: r.hour_from }),
        date_time_to: formatDateISOWithoutTimezone({ date: r.date_to, hours: r.hour_to })
      }))
    };
  }
}
class RequestReadBookingsOwnerFilters extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/v4/bookings-owner/options/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBrands extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/brands/", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "500");
    }
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadBrandsModels extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/brands-models/", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "500");
    }
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadCamper extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idCamper, ...query } = this._query;
    const url = new URL(`/3/products/${idCamper}/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return parseCamper(responseData);
  }
}
class RequestReadCamperAvailabilities extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idCamper, date_from, date_to, ...query } = this._query;
    const url = new URL(`/3/products/${this._query.idCamper}/availabilities/`, baseURL);
    setURLSearchParams({
      url,
      query: {
        ...query,
        date_from: formatDateISOWithoutTime({ date: date_from }),
        date_to: formatDateISOWithoutTime({ date: date_to })
      }
    });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadCamperPeriods extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { idCamper, ...query } = this._query;
    const url = new URL(`/3/products/${idCamper}/calendar/periods/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    responseData.forEach(parsePeriod);
    return responseData;
  }
}
class RequestReadCamperPrices extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._query.idCamper}/calendar/prices/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadCamperPricing extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._query.idCamper}/prices-details/`, baseURL);
  }
  parseResult(responseData) {
    const { deposit_mean, ...rest } = responseData;
    return {
      ...rest,
      deposit_mean: stringToArray(deposit_mean)
    };
  }
}
class RequestReadCamperProgress extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`v4/products/${this._query.idCamper}/progress/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
const parseSearchResultItem = ({ price_per_day, final_booking_price, original_booking_price, hour_from, hour_to, ...rest }) => {
  return {
    ...rest,
    price_per_day: price_per_day !== null ? parseFloat(price_per_day) : null,
    final_booking_price: final_booking_price !== null ? parseFloat(final_booking_price) : null,
    original_booking_price: original_booking_price !== null ? parseFloat(original_booking_price) : null,
    hour_from: hour_from ? parseInt(hour_from.substring(0, 2)) : null,
    hour_to: hour_to ? parseInt(hour_to.substring(0, 2)) : null
  };
};
class RequestReadCampers extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/products/", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "20");
    }
    return url;
  }
  parseResult(responseData) {
    const { results, ...origin } = responseData;
    return {
      ...origin,
      results: results.map(parseSearchResultItem)
    };
  }
}
class RequestReadCampersSearch extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/campers/search", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "20");
    }
    return url;
  }
  parseResult(responseData) {
    const { results, ...origin } = responseData;
    return {
      ...origin,
      results: results.map(parseSearchResultItem)
    };
  }
}
class RequestReadCampersOfMine extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/products/me/paginated/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    const { results, ...origin } = responseData;
    return {
      ...origin,
      results: results.map(parseCamper)
    };
  }
}
class RequestReadCampersOfMineFilters extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/3/products/me/filters/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadCamperStatistics extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._query.idCamper}/stats/`, baseURL);
  }
  parseResult(responseData) {
    responseData.visit_stats.forEach((entry) => {
      entry[0] = formatDateISOWithoutTimezone({ date: entry[0] });
    });
    responseData.print_stats.forEach((entry) => {
      entry[0] = formatDateISOWithoutTimezone({ date: entry[0] });
    });
    return responseData;
  }
}
const parseChat = (responseData) => ({
  ...responseData,
  date_time_from: formatDateISOWithoutTimezone({ date: responseData.date_from }),
  date_time_to: formatDateISOWithoutTimezone({ date: responseData.date_to }),
  messages: responseData.messages.map(parseChatMessage)
});
class RequestReadChat extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/chats/${this._query.idChat}/`, baseURL);
  }
  parseResult(responseData) {
    return parseChat(responseData);
  }
}
class RequestReadChats extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/chats/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    return {
      ...responseData,
      results: responseData.results.map(parseChat)
    };
  }
}
const parseDocument = (responseData) => {
  return {
    ...responseData,
    expiration_date: formatDateISOWithoutTimezone({ date: responseData.expiration_date }),
    real_edition_date: formatDateISOWithoutTimezone({ date: responseData.real_edition_date })
  };
};
class RequestReadDocument extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/docs/${this._query.idDocument}/`, baseURL);
  }
  parseResult(responseData) {
    return parseDocument(responseData);
  }
}
class RequestReadDocuments extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/docs/${this._query.relatedTo}/${this._query.id}/`, baseURL);
  }
  parseResult(responseData) {
    return responseData.map(parseDocument);
  }
}
class RequestReadFAQs extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/faq/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadFAQsByUserType extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { destination_user, ...query } = this._query;
    const url = new URL(`/3/faq/user/${this._query.destination_user}/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadGeolocations extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/geolocation/places/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadGoboonyDuplicationRedirecUrl extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL(`/v4/goboony/listing/${this._query.idCamper}/redirect-url/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadIban extends YscApiRequestRead {
  constructor() {
    super(...arguments);
    __publicField(this, "silentCatch", true);
  }
  getEndpointURL(baseURL) {
    return new URL("/3/mangopay/iban/", baseURL);
  }
  parseResult(responseData) {
    return parseIban(responseData);
  }
}
const parseInsurance = (responseData) => {
  const { damage_to_vehicle_price, damage_high_part_price, burglary_price, broken_glass_price, objects_protection_price, ...rest } = responseData;
  return {
    ...rest,
    damage_to_vehicle_price: damage_to_vehicle_price ? parseFloat(damage_to_vehicle_price) : null,
    damage_high_part_price: damage_high_part_price ? parseFloat(damage_high_part_price) : null,
    burglary_price: burglary_price ? parseFloat(burglary_price) : null,
    broken_glass_price: broken_glass_price ? parseFloat(broken_glass_price) : null,
    objects_protection_price: objects_protection_price ? parseFloat(objects_protection_price) : null
  };
};
class RequestReadInsurance extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/insurance/${this._query.slug}/`, baseURL);
  }
  parseResult(responseData) {
    return parseInsurance(responseData);
  }
}
class RequestReadInsuranceConditions extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/insurance/condition/product/${this._query.idProduct}/`, baseURL);
  }
  parseResult(responseData) {
    return parseInsuranceConditions(responseData);
  }
}
class RequestReadInsuranceRentalCover extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/rental-cover/conditions/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    const {
      damage_to_vehicle_price,
      burglary_price,
      broken_glass_price,
      internal_damage_price,
      ...rest
    } = responseData;
    return {
      ...rest,
      damage_to_vehicle_price: parseFloat(damage_to_vehicle_price),
      burglary_price: parseFloat(burglary_price),
      broken_glass_price: parseFloat(broken_glass_price),
      internal_damage_price: parseFloat(internal_damage_price)
    };
  }
}
class RequestReadInsurances extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/insurance/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    var _a;
    return {
      choices: ((_a = responseData.choices) == null ? void 0 : _a.map(parseInsurance)) ?? null
    };
  }
}
class RequestReadInsurancesSimilar extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const { slug, ...query } = this._query;
    const url = new URL(`/3/insurance/similar/${slug}/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  parseResult(responseData) {
    return responseData.map(parseInsurance);
  }
}
class RequestReadLeadSource extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/leads/lead-sources/${this._query.id}/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadManufacturers extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/manufacturers/", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "500");
    }
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadManufacturersModels extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/manufacturers-models/", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "500");
    }
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadMoneyboxes extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/3/moneyboxes/", baseURL);
  }
  parseResult(responseData) {
    responseData.forEach((m) => {
      m.expire_at = formatDateISOWithoutTimezone({ date: m.expire_at });
    });
    return responseData;
  }
}
const parsePaymentHistory = (paymentHistory) => {
  const { execution_date, ...rest } = paymentHistory;
  return {
    ...rest,
    execution_date: formatDateISOWithoutTimezone({ date: execution_date })
  };
};
class RequestReadPayins extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/mangopay/payin/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    responseData.list.forEach(parsePaymentHistory);
    return responseData;
  }
}
class RequestReadPayouts extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/mangopay/payout/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    responseData.list.forEach(parsePaymentHistory);
    return responseData;
  }
}
class RequestReadPhoneCertification extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/3/certify-phone/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
const parseReferential = (responseData, lang) => {
  const root = responseData[lang];
  return {
    guide_owner: root.guide_owner,
    guide_guest: root.guide_guest,
    email_address: root.email_address,
    care_email_address: root.care_email_address,
    cancellation_insurance_terms: root.cancellation_insurance_terms,
    can_publish_camper: root.can_publish_camper,
    site_terms: root.site_terms,
    phone_number: root.phone_number,
    payment_type_available: root.payment_type_available,
    second_driver_fees: root.second_driver_fees,
    second_driver_fees_currency: root.second_driver_fees_currency,
    mangopay: root.mangopay,
    min_days_future_rental_choices: root.min_days_future_rental_choices,
    max_months_future_rental_choices: root.max_months_future_rental_choices,
    booking_min_days: root.booking_min_days,
    calendar_rules: root.calendar_rules,
    options: {
      reason_period_unavailable: root.reason_period_unavailable.map(mapValueText),
      reason_period_unavailable_edition: (root.reason_period_unavailable_edition ?? root.reason_period_unavailable).map(mapValueText),
      cancel_reason_owner: root.cancel_reason_owner.map(mapValueText),
      cancel_reason_guest: root.cancel_reason_guest.map(mapValueText),
      reason_suspend_delete_ad: root.reason_suspend_delete_ad.map(mapValueText),
      deposit_mean: root.deposit_mean.map(mapValueText),
      civility: [{ value: null, text: "" }, ...root.civility.map(mapValueText)],
      currency: [{ value: null, text: "" }, ...root.currency.map(([code, symbol]) => ({ value: code, text: `${symbol} (${code})` }))],
      /** @deprecated use spoken_language_list or  site_language_list instead */
      language: root.language.map((v) => ({ value: v[0].toLowerCase(), text: v[1] })),
      spoken_language_list: root.spoken_language_list.map(mapValueText),
      site_language_list: root.site_language_list.map((value) => ({ value: value.toLowerCase(), text: value })),
      vehicle_type_choices: root.vehicle_type_choices.map((v) => ({
        value: +v[0],
        text: v[1]
      })),
      additive_types: root.additive_types.map(mapValueText),
      energie_types: root.energie_types.map(mapValueText),
      sleeping_types: root.sleeping_types.map(mapValueText),
      mileageEstimate: root.km_values.map(mapValueText),
      consumption_values: root.consumption_values.map(mapValueText),
      fuel_types: root.fuel_types.map(([code, text]) => ({ value: code, text: text.toLowerCase() })),
      country: computeCountryOptions(root.countries, lang),
      countries_main: computeCountryOptions(root.ordered_countries_main, lang),
      countries_others: computeCountryOptions(root.ordered_countries_others),
      hour_from: [BOOKING_HOUR_FROM_MORNING, BOOKING_HOUR_FROM_AFTERNOON].map((value) => ({
        value,
        text: Date.UTC(0, 0, 0, value, 0, 0, 0)
      })),
      hour_to: [BOOKING_HOUR_TO_MORNING, BOOKING_HOUR_TO_AFTERNOON].map((value) => ({
        value,
        text: Date.UTC(0, 0, 0, value, 0, 0, 0)
      })),
      professional_types: [
        { value: null, text: "" },
        { value: USER_PROFESIONAL_TYPES.SOLE_TRADER, text: "sole_trader" },
        { value: USER_PROFESIONAL_TYPES.BUSINESS, text: "business" }
      ],
      gear_types: root.gear_types.map(([code, text]) => ({
        value: code,
        text: text.toLowerCase()
      })),
      instant_booking_deactivation_reasons: root.instant_booking_deactivation_reasons.map(mapValueText)
    },
    countries_booking_eligibility: root.countries_booking_eligibility,
    countries_crossed_search_eligibility: root.countries_crossed_search_eligibility,
    maps: {
      option_display: root.option_display.reduce((acc, cur) => {
        acc[cur.tag] = cur;
        return acc;
      }, {}),
      currency: root.currency.map((c) => c[0]).reduce((acc, currency) => {
        acc[currency] = {
          currency,
          currencyDisplay: "symbol",
          style: "currency"
        };
        return acc;
      }, {}),
      currency_symbol: root.currency.reduce(reduceDoubleArray, {}),
      deposit_mean: root.deposit_mean.reduce(reduceDoubleArray, {}),
      language: root.language.reduce((acc, cur) => {
        acc[cur[0].toLowerCase()] = cur[1];
        return acc;
      }, {}),
      reason_period_unavailable: root.reason_period_unavailable.reduce(reduceDoubleArray, {}),
      vehicle_image_type: root.vehicle_image_type.reduce(reduceDoubleArray, {}),
      vehicle_type_as_int: {
        [CAMPER_TYPES_INTEGER.LOWPROFILE]: { key: "lowprofile" },
        [CAMPER_TYPES_INTEGER.COACHBUILT]: { key: "coachbuilt" },
        [CAMPER_TYPES_INTEGER.ACLASS]: { key: "aclass" },
        [CAMPER_TYPES_INTEGER.CAMPERVAN]: { key: "campervan" },
        [CAMPER_TYPES_INTEGER.VAN]: { key: "van" },
        [CAMPER_TYPES_INTEGER.CARAVAN]: { key: "caravan" },
        [CAMPER_TYPES_INTEGER.OTHER]: { key: "other" }
      },
      vehicle_equipments: {
        driving_confort: [
          { category: "driving", value: "automatic_gear" },
          { category: "driving", value: "gps" },
          { category: "driving", value: "parking_aid_radar" },
          { category: "driving", value: "parking_aid_camera" },
          { category: "driving", value: "baby_seat" },
          { category: "driving", value: "bluetooth" },
          { category: "driving", value: "ipod_input" }
        ],
        on_board: [
          { category: "kitchen_meal", value: "cooking" },
          { category: "kitchen_meal", value: "sink" },
          { category: "kitchen_meal", value: "dishes_kit" },
          { category: "kitchen_meal", value: "fridge" },
          { category: "kitchen_meal", value: "freezer" },
          { category: "kitchen_meal", value: "oven" },
          { category: "kitchen_meal", value: "cookware" },
          { category: "life_on_board", value: "bedding" },
          { category: "driving", value: "ac" },
          { category: "life_on_board", value: "ac_premium" },
          { category: "driving", value: "heating" },
          { category: "life_on_board", value: "tv" },
          { category: "toilet", value: "shower_int" },
          { category: "toilet", value: "wc" },
          { category: "toilet", value: "bathroom_sink" }
        ],
        outdoor: [
          { category: "outside", value: "side_blind" },
          { category: "outside", value: "camping_table" },
          { category: "outside", value: "bicycle" },
          { category: "outside", value: "sout_luggage" },
          { category: "outside", value: "sout_garage" },
          { category: "outside", value: "sout_adjustable" },
          { category: "autonomy-details", value: "solar" },
          { category: "outside", value: "barbecue" },
          { category: "outside", value: "towbar" }
        ]
      }
    }
  };
};
class RequestReadReferential extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/referential/", baseURL);
  }
  parseResult(responseData) {
    return parseReferential(responseData, this._query.lang);
  }
}
class RequestReadReview extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/review/${this._query.idReview}/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadReviewsNeeded extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/reviews-needed/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    const { results, ...rest } = responseData;
    return {
      ...rest,
      results: results.map((reviewNeeded) => ({
        ...reviewNeeded,
        date_time_from: formatDateISOWithoutTimezone({
          date: reviewNeeded.date_from,
          hours: reviewNeeded.hour_from
        }),
        date_time_to: formatDateISOWithoutTimezone({
          date: reviewNeeded.date_to,
          hours: reviewNeeded.hour_to
        }),
        guest: {
          id: reviewNeeded.guest_id,
          first_name: reviewNeeded.guest_first_name,
          photo: reviewNeeded.guest_picture
        },
        owner: {
          id: reviewNeeded.owner_id,
          first_name: reviewNeeded.owner_first_name,
          photo: reviewNeeded.owner_picture
        }
      }))
    };
  }
}
const parseReview = (responseData) => {
  return {
    ...responseData,
    date_time_published: formatDateISOWithTimezone({ dateString: responseData.published_date })
  };
};
class RequestReadReviews extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/v4/reviews/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    const { results, ...rest } = responseData;
    return {
      ...rest,
      results: results.map(parseReview)
    };
  }
}
class RequestReadRouting extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/routing/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    return responseData[this._query.lang];
  }
}
class RequestReadSearchLocationInfos extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/info_location/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadSearchLocations extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/search_locations/", baseURL);
    setURLSearchParams({ url, query: this._query });
    return url;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadStatistics extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/v4/reviews/stats/", baseURL);
  }
  parseResult(responseData) {
    return {
      ...responseData,
      average: parseFloat(responseData.average)
    };
  }
}
const parseUBODeclaration = (responseData) => {
  let status;
  switch (responseData.status) {
    case "C": {
      status = DOCUMENT_STATES.CREATED;
      break;
    }
    case "A": {
      status = DOCUMENT_STATES.VALIDATION_ASKED;
      break;
    }
    case "V": {
      status = DOCUMENT_STATES.VALIDATED;
      break;
    }
    case "R": {
      status = DOCUMENT_STATES.REFUSED;
      break;
    }
    case "O": {
      status = DOCUMENT_STATES.EXPIRED;
      break;
    }
    case "I": {
      status = DOCUMENT_STATES.REFUSED;
      break;
    }
    case null: {
      status = DOCUMENT_STATES.CREATED;
    }
  }
  return {
    ...responseData,
    id: DOCUMENT_TYPES.UBO_DECLARATION,
    document_type: DOCUMENT_TYPES.UBO_DECLARATION,
    status
  };
};
class RequestReadUBODeclaration extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/3/mangopay/ubo-declaration/", baseURL);
  }
  parseResult(responseData) {
    return parseUBODeclaration(responseData);
  }
}
class RequestReadUBODeclarationUsers extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/mangopay/ubo-declaration/${this._query.idUbo}/ubo/`, baseURL);
  }
  parseResult(responseData) {
    return responseData.map(parseUBODeclarationUser);
  }
}
class RequestReadUser extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/users/${this._query.id}/`, baseURL);
  }
  parseResult(responseData) {
    return parseUser(responseData);
  }
}
class RequestReadUserCompany extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/v4/users/me/company/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadUserLegalOwner extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/v4/users/me/legal/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadUserMe extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/3/users/me/", baseURL);
  }
  parseResult(responseData) {
    return parseUser(responseData);
  }
}
class RequestReadVehicle extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._query.idVehicle}/`, baseURL);
  }
  parseResult(responseData) {
    return parseVehicle(responseData);
  }
}
class RequestCreateVehiclePhoto extends YscApiRequestCreateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/photos/`, baseURL);
  }
  formatPayload() {
    return this._payload.photo;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadVehicleEquipments extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._query.idVehicle}/equipments/`, baseURL);
  }
  parseResult(responseData) {
    return parseVehicleEquipments(responseData);
  }
}
class RequestReadVehiclePhotos extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._query.idVehicle}/photos/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadVehicleSleepings extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._query.idVehicle}/sleepings/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestReadWishlist extends YscApiRequestRead {
  constructor({ query }) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/wishlist/${this._query.slug}/`, baseURL);
  }
  parseResult(responseData) {
    return parseWishlist(responseData);
  }
}
class RequestReadWishlists extends YscApiRequestRead {
  getEndpointURL(baseURL) {
    return new URL("/3/wishlists/", baseURL);
  }
  parseResult(responseData) {
    return responseData.map(parseWishlist);
  }
}
class RequestReadYesboonySearch extends YscApiRequestRead {
  constructor({ query } = {}) {
    super({ query });
  }
  getEndpointURL(baseURL) {
    const url = new URL("/3/yesboony/search/", baseURL);
    setURLSearchParams({ url, query: this._query });
    if (!url.searchParams.has("page_size")) {
      url.searchParams.set("page_size", "20");
    }
    return url;
  }
  parseResult(responseData) {
    const { results, ...origin } = responseData;
    return {
      ...origin,
      results: results.map(parseSearchResultItem)
    };
  }
}
class RequestUpdateBookingGuest extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...payload } = this._payload;
    return payload;
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingGuestCancellation extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/cancel/`, baseURL);
  }
  formatPayload() {
    const { idBookingGuest, ...payload } = this._payload;
    return payload;
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingGuestExtensionsCancellation extends YscApiRequestCreate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/extensions/cancel/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingGuestExtensionsValidation extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/extensions/validate/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingGuestPrices extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    const { idBookingGuest, ...query } = this._payload;
    const url = new URL(`/3/my-requests/${idBookingGuest}/prices/`, baseURL);
    setURLSearchParams({ url, query });
    return url;
  }
  formatPayload() {
    const { idBookingGuest, ...payload } = this._payload;
    return payload;
  }
  parseResult(responseData) {
    return parseBookingPrices(responseData);
  }
}
class RequestUpdateBookingGuestUpgradesCancellation extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/upgrades/cancel/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingGuestUpgradesValidation extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/upgrades/validate/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingGuestValidation extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-guest/${this._payload.idBookingGuest}/validate/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingGuest(responseData);
  }
}
class RequestUpdateBookingOwnerApproval extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-owner/${this._payload.idBookingOwner}/accept/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingOwner(responseData);
  }
}
class RequestUpdateBookingOwnerExtensionApproval extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-owner/${this._payload.idBookingOwner}/extensions/accept/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingOwner(responseData);
  }
}
class RequestUpdateBookingOwnerExtensionRefusal extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-owner/${this._payload.idBookingOwner}/extensions/refuse/`, baseURL);
  }
  parseResult(responseData) {
    return parseBookingOwner(responseData);
  }
}
class RequestUpdateBookingOwnerRefusal extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/bookings-owner/${this._payload.idBookingOwner}/refuse/`, baseURL);
  }
  formatPayload() {
    const { idBookingOwner, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseBookingOwner(responseData);
  }
}
class RequestUpdateCamper extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/update/preferences/`, baseURL);
  }
  formatPayload() {
    const { idCamper, deposit_mean, vehicle_owner_language_spoke, abroad_allowed, pets_allowed, smoking_allowed, ...rest } = this._payload;
    const formatted = rest;
    if (deposit_mean) {
      formatted.deposit_mean = arrayToStringOrNull(deposit_mean);
    }
    if (vehicle_owner_language_spoke) {
      formatted.vehicle_owner_language_spoke = arrayToStringOrNull(vehicle_owner_language_spoke);
    }
    const mapAcceptanceStateToTrilean = (acceptanceStatus) => {
      const { ALLOWED, NOT_ALLOWED } = OWNER_ACCEPTANCE_STATES;
      return acceptanceStatus === ALLOWED ? true : acceptanceStatus === NOT_ALLOWED ? false : null;
    };
    if (abroad_allowed) {
      formatted.abroad_allowed = mapAcceptanceStateToTrilean(abroad_allowed);
    }
    if (pets_allowed) {
      formatted.pets_allowed = mapAcceptanceStateToTrilean(pets_allowed);
    }
    if (smoking_allowed) {
      formatted.smoking_allowed = mapAcceptanceStateToTrilean(smoking_allowed);
    }
    return formatted;
  }
  parseResult(responseData) {
    return parseCamper(responseData);
  }
}
class RequestUpdateCamperPricingOption extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/options/`, baseURL);
  }
  formatPayload() {
    const { idCamper, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateCamperPeriod extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/calendar/periods/${this._payload.idPeriod}/`, baseURL);
  }
  formatPayload() {
    const { idCamper, idPeriod, dates_0, dates_1, ...rest } = this._payload;
    return {
      ...rest,
      dates_0: formatDateISOWithoutTime({ date: dates_0 }),
      dates_1: formatDateISOWithoutTime({ date: dates_1 })
    };
  }
  parseResult(responseData) {
    return parsePeriod(responseData);
  }
}
class RequestUpdateCamperPrice extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/calendar/prices/${this._payload.idPrice}/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateCamperPricing extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/products/${this._payload.idCamper}/prices-details/`, baseURL);
  }
  formatPayload() {
    const { idCamper, deposit_mean, ...rest } = this._payload;
    return {
      ...rest,
      deposit_mean: arrayToStringOrNull(deposit_mean)
    };
  }
  parseResult(responseData) {
    const { deposit_mean, ...rest } = responseData;
    return {
      ...rest,
      deposit_mean: stringToArray(deposit_mean)
    };
  }
}
class RequestUpdateChatMessage extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/chats/messages/${this._payload.idMessage}/`, baseURL);
  }
  parseResult(responseData) {
    return parseChatMessage(responseData);
  }
}
const formatDocument = (payload) => {
  return {
    ...payload,
    expiration_date: formatDateISOWithoutTime({ date: payload.expiration_date }),
    real_edition_date: formatDateISOWithoutTime({ date: payload.real_edition_date })
  };
};
class RequestUpdateDocument extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/docs/${this._payload.id}/`, baseURL);
  }
  formatPayload() {
    return formatDocument(this._payload);
  }
  parseResult(responseData) {
    return parseDocument(responseData);
  }
}
class RequestUpdateInsuranceConditions extends YscApiRequestUpdateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/insurance/condition/product/${this._payload.idProduct}/`, baseURL);
  }
  formatPayload() {
    return this._payload.form;
  }
  parseResult(responseData) {
    return parseInsuranceConditions(responseData);
  }
}
class RequestUpdateReview extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/review/${this._payload.idReview}/`, baseURL);
  }
  formatPayload() {
    const { idReview, ...payload } = this._payload;
    return payload;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateReviewPhoto extends YscApiRequestUpdateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/review/${this._payload.idReview}/photo/`, baseURL);
  }
  formatPayload() {
    return this._payload.image;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateReviewReport extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/review/${this._payload.idReview}/report/`, baseURL);
  }
  formatPayload() {
    return void 0;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateUBODeclaration extends YscApiRequestUpdate {
  getEndpointURL(baseURL) {
    return new URL("/3/mangopay/ubo-declaration/", baseURL);
  }
  parseResult(responseData) {
    return parseUBODeclaration(responseData);
  }
}
class RequestUpdateUBODeclarationUser extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/mangopay/ubo-declaration/${this._payload.idUbo}/ubo/${this._payload.idMangopayUser}/`, baseURL);
  }
  formatPayload() {
    const { idMangopayUser, ...payload } = this._payload;
    return formatUBODeclarationUser(payload);
  }
  parseResult(responseData) {
    return parseUBODeclarationUser(responseData);
  }
}
class RequestUpdateUserCompany extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/users/me/company/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateUserLegalOwner extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  formatPayload() {
    const { borned_on: raw_borned_on, ...rest } = this._payload;
    let borned_on = void 0;
    if (raw_borned_on) {
      borned_on = formatDateISOWithoutTime({ date: raw_borned_on });
    }
    return {
      ...rest,
      borned_on
    };
  }
  getEndpointURL(baseURL) {
    return new URL("/v4/users/me/legal/", baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateUserMe extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL("/3/users/me/", baseURL);
  }
  formatPayload() {
    const { borned_on: raw_borned_on, ...rest } = this._payload;
    let borned_on = void 0;
    if (raw_borned_on) {
      borned_on = formatDateISOWithoutTime({ date: raw_borned_on });
    }
    return {
      ...rest,
      borned_on
    };
  }
  parseResult(responseData) {
    return parseUser(responseData);
  }
}
class RequestUpdateVehicle extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/`, baseURL);
  }
  formatPayload() {
    const { idVehicle, date, ...rest } = this._payload;
    return {
      ...rest,
      date: formatDateISOWithoutTime({ date })
    };
  }
  parseResult(responseData) {
    return parseVehicle(responseData);
  }
}
class RequestUpdateVehicleEquipments extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/equipments/`, baseURL);
  }
  formatPayload() {
    const { idVehicle, ...rest } = this._payload;
    const toTransform = ["cooking_energies", "heating_energies", "fridge_energies", "electric_plug_types"];
    toTransform.forEach((key) => {
      if (Array.isArray(rest[key]) && !rest[key].length) {
        rest[key] = null;
      }
    });
    return rest;
  }
  parseResult(responseData) {
    return parseVehicleEquipments(responseData);
  }
}
class RequestUpdateVehiclePhoto extends YscApiRequestUpdateMultipartFormData {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/photos/${this._payload.idPhoto}/`, baseURL);
  }
  formatPayload() {
    return this._payload.photo;
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateVehiclePhotoRotation extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/v4/vehicles/${this._payload.idVehicle}/photos/${this._payload.idPhoto}/rotate/`, baseURL);
  }
  parseResult(responseData) {
    return responseData;
  }
}
class RequestUpdateWishlist extends YscApiRequestUpdate {
  constructor({ payload }) {
    super({ payload });
  }
  getEndpointURL(baseURL) {
    return new URL(`/3/wishlist/${this._payload.slug}/`, baseURL);
  }
  formatPayload() {
    const { slug, ...rest } = this._payload;
    return rest;
  }
  parseResult(responseData) {
    return parseWishlist(responseData);
  }
}
const arrayToStringOrNull = (a) => a.length ? a.join(",") : null;
const computeCountryOptions = (countries, locale = null) => {
  const countryOptions = Object.entries(countries).map(([value, text]) => ({ value, text })).sort((a, b) => a.text.localeCompare(b.text));
  locale = locale ? locale.slice(-2).toUpperCase() : null;
  const i = countryOptions.findIndex((opt) => opt.value === locale);
  if (i >= 0) {
    const option = countryOptions[i];
    countryOptions.splice(i, 1);
    countryOptions.splice(0, 0, option);
  }
  return countryOptions;
};
var BOOKING_GUEST_CANCEL_REASONS = /* @__PURE__ */ ((BOOKING_GUEST_CANCEL_REASONS2) => {
  BOOKING_GUEST_CANCEL_REASONS2["REASON_OWNER"] = "REASON_OWNER";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_CORONA"] = "REASON_CORONA";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_OTHER"] = "REASON_OTHER";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_DUPLICATE_BOOKING"] = "DUPLICATE";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_COMPETITORS"] = "COMPETITORS";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_CAMPER_COMPETITORS"] = "OTHER_COMPETITORS";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_CANCEL_HOLIDAYS"] = "CANCEL_HOLIDAYS";
  BOOKING_GUEST_CANCEL_REASONS2["REASON_CASUALTY"] = "REASON_CASUALTY";
  return BOOKING_GUEST_CANCEL_REASONS2;
})(BOOKING_GUEST_CANCEL_REASONS || {});
var BOOKING_OWNER_REFUSE_REASONS = /* @__PURE__ */ ((BOOKING_OWNER_REFUSE_REASONS2) => {
  BOOKING_OWNER_REFUSE_REASONS2["REASON_KM"] = "REASON_KM";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_DATE"] = "REASON_DATE";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_UNAVAILABLE_CALENDAR"] = "REASON_UNAVAILABLE";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_UNAVAILABLE_BOOKING"] = "REASON_UNAVA_ASKED";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_DOWN"] = "REASON_DOWN";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_SNOW"] = "REASON_SNOW";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_BABY"] = "REASON_BABY";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_ANIMAL"] = "REASON_ANIMAL";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_SMOKING"] = "REASON_SMOKING";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_TRIP"] = "REASON_TRIP";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_SEAT"] = "REASON_SEAT";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_PURPOSE"] = "RESON_PURPOSE";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_GUEST"] = "REASON_GUEST";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_OTHER"] = "REASON_OTHER";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_DOUBT_COVID"] = "REASON_DOUBT_COVID";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_OLD_PRICE"] = "REASON_OLD_PRICE";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_UNAVAILABLE_FOR_INVENTORY"] = "REASON_UNAVAILABLE_FOR_INVENTORY";
  BOOKING_OWNER_REFUSE_REASONS2["REASON_UNAVAILABLE_FOR_CHECK_IN"] = "REASON_UNAV_CHECKN";
  return BOOKING_OWNER_REFUSE_REASONS2;
})(BOOKING_OWNER_REFUSE_REASONS || {});
var BOOKING_OWNER_CANCEL_REASONS = /* @__PURE__ */ ((BOOKING_OWNER_CANCEL_REASONS2) => {
  BOOKING_OWNER_CANCEL_REASONS2["REASON_BREAKDOWN"] = "REASON_BREAKDOWN";
  BOOKING_OWNER_CANCEL_REASONS2["REASON_MOT"] = "REASON_MOT";
  BOOKING_OWNER_CANCEL_REASONS2["REASON_UNAVAILABLE"] = "REASON_UNAVAILABLE";
  BOOKING_OWNER_CANCEL_REASONS2["REASON_SALE"] = "REASON_SALE";
  BOOKING_OWNER_CANCEL_REASONS2["REASON_PERSONAL"] = "REASON_PERSONAL";
  BOOKING_OWNER_CANCEL_REASONS2["REASON_PRICES"] = "REASON_PRICES";
  BOOKING_OWNER_CANCEL_REASONS2["REASON_OTHER"] = "REASON_OTHER";
  return BOOKING_OWNER_CANCEL_REASONS2;
})(BOOKING_OWNER_CANCEL_REASONS || {});
var BOOKING_OWNER_CANCEL_STATE = /* @__PURE__ */ ((BOOKING_OWNER_CANCEL_STATE2) => {
  BOOKING_OWNER_CANCEL_STATE2["CREATED"] = "CREATED";
  BOOKING_OWNER_CANCEL_STATE2["WAITING"] = "WAITING";
  BOOKING_OWNER_CANCEL_STATE2["ACCEPTED"] = "ACCEPTED";
  BOOKING_OWNER_CANCEL_STATE2["REJECTED"] = "REJECTED";
  return BOOKING_OWNER_CANCEL_STATE2;
})(BOOKING_OWNER_CANCEL_STATE || {});
var REFUND_REASONS = /* @__PURE__ */ ((REFUND_REASONS2) => {
  REFUND_REASONS2["REASON_EMERGENCY"] = "REASON_CORONA";
  REFUND_REASONS2["REASON_OTHER"] = "REASON_OTHER";
  REFUND_REASONS2["REASON_OWNER"] = "REASON_OWNER";
  return REFUND_REASONS2;
})(REFUND_REASONS || {});
var CAMPER_SUSPEND_REASONS = /* @__PURE__ */ ((CAMPER_SUSPEND_REASONS2) => {
  CAMPER_SUSPEND_REASONS2["DOUBLE_PRODUCT"] = "DELETE_DOUBLE";
  CAMPER_SUSPEND_REASONS2["VEHICLE_SOLD"] = "DELETE_SOLD";
  CAMPER_SUSPEND_REASONS2["WANT_TO_SOLD_VEHICLE"] = "DELETE_W_SOLD";
  CAMPER_SUSPEND_REASONS2["VEHICLE_KO"] = "DELETE_HS";
  CAMPER_SUSPEND_REASONS2["NOT_ENOUGH_BOOKINGS"] = "DELETE_NO_BK";
  CAMPER_SUSPEND_REASONS2["DISAGREE_YESCAPA"] = "DELETE_SITE";
  CAMPER_SUSPEND_REASONS2["BAD_EXPERIENCE_YESCAPA"] = "DELETE_BAD_EXP";
  CAMPER_SUSPEND_REASONS2["PERSONNAL_USE"] = "DELETE_PERSO";
  CAMPER_SUSPEND_REASONS2["OTHER"] = "REASON_NONE";
  return CAMPER_SUSPEND_REASONS2;
})(CAMPER_SUSPEND_REASONS || {});
const BOOKING_HOUR_FROM_MORNING = 9;
const BOOKING_HOUR_FROM_AFTERNOON = 14;
const BOOKING_HOUR_TO_MORNING = 12;
const BOOKING_HOUR_TO_AFTERNOON = 20;
const DEPOSIT_MEANS = ["card", "check", "cash", "transfer", "others"];
var USER_PROFESIONAL_TYPES = /* @__PURE__ */ ((USER_PROFESIONAL_TYPES2) => {
  USER_PROFESIONAL_TYPES2["SOLE_TRADER"] = "SOLETRADER";
  USER_PROFESIONAL_TYPES2["BUSINESS"] = "BUSINESS";
  USER_PROFESIONAL_TYPES2["PARTNERSHIP_DE"] = "PARTNERSHIP_DE";
  USER_PROFESIONAL_TYPES2["PARTNERSHIP_NL"] = "PARTNERSHIP_NL";
  return USER_PROFESIONAL_TYPES2;
})(USER_PROFESIONAL_TYPES || {});
var CAMPER_TYPES_INTEGER = /* @__PURE__ */ ((CAMPER_TYPES_INTEGER2) => {
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["LOWPROFILE"] = 1] = "LOWPROFILE";
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["COACHBUILT"] = 2] = "COACHBUILT";
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["ACLASS"] = 3] = "ACLASS";
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["CAMPERVAN"] = 4] = "CAMPERVAN";
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["VAN"] = 5] = "VAN";
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["CARAVAN"] = 6] = "CARAVAN";
  CAMPER_TYPES_INTEGER2[CAMPER_TYPES_INTEGER2["OTHER"] = 7] = "OTHER";
  return CAMPER_TYPES_INTEGER2;
})(CAMPER_TYPES_INTEGER || {});
var CAMPER_TYPES = /* @__PURE__ */ ((CAMPER_TYPES2) => {
  CAMPER_TYPES2["LOWPROFILE"] = "LowProfile";
  CAMPER_TYPES2["COACHBUILT"] = "CoachBuilt";
  CAMPER_TYPES2["ACLASS"] = "AClass";
  CAMPER_TYPES2["CAMPERVAN"] = "Campervan";
  CAMPER_TYPES2["VAN"] = "Van";
  CAMPER_TYPES2["CARAVAN"] = "Caravan";
  CAMPER_TYPES2["OTHER"] = "Other";
  return CAMPER_TYPES2;
})(CAMPER_TYPES || {});
var CAMPER_COMPLETION_TYPES = /* @__PURE__ */ ((CAMPER_COMPLETION_TYPES2) => {
  CAMPER_COMPLETION_TYPES2["vehicle"] = "vehicle";
  CAMPER_COMPLETION_TYPES2["prices"] = "prices";
  CAMPER_COMPLETION_TYPES2["technical"] = "technical";
  CAMPER_COMPLETION_TYPES2["account"] = "account";
  CAMPER_COMPLETION_TYPES2["documents"] = "documents";
  return CAMPER_COMPLETION_TYPES2;
})(CAMPER_COMPLETION_TYPES || {});
var TRACKING_EVENT_TYPES = /* @__PURE__ */ ((TRACKING_EVENT_TYPES2) => {
  TRACKING_EVENT_TYPES2["PAGE_VIEWED"] = "page_viewed";
  TRACKING_EVENT_TYPES2["CAMPER_VIEWED"] = "camper_viewed";
  TRACKING_EVENT_TYPES2["CAMPER_PAGE_VIEWED"] = "camper_page_viewed";
  return TRACKING_EVENT_TYPES2;
})(TRACKING_EVENT_TYPES || {});
var BOOKING_META_STATES = /* @__PURE__ */ ((BOOKING_META_STATES2) => {
  BOOKING_META_STATES2["TO_DO"] = "todo";
  BOOKING_META_STATES2["CONFIRMED"] = "confirmed";
  BOOKING_META_STATES2["WAITING"] = "waiting";
  BOOKING_META_STATES2["ARCHIVED"] = "archived";
  return BOOKING_META_STATES2;
})(BOOKING_META_STATES || {});
var CAMPER_STATES = /* @__PURE__ */ ((CAMPER_STATES2) => {
  CAMPER_STATES2["CREATED"] = "created";
  CAMPER_STATES2["TO_MODERATE"] = "to_moderate";
  CAMPER_STATES2["PUBLISHED"] = "published";
  CAMPER_STATES2["BOOKABLE"] = "bookable";
  CAMPER_STATES2["UNPUBLISHED"] = "unpublished";
  CAMPER_STATES2["UNPUBLISHED_OWNER"] = "unpublished_owner";
  CAMPER_STATES2["DELETED"] = "deleted";
  CAMPER_STATES2["DELETED_OWNER"] = "deleted_owner";
  return CAMPER_STATES2;
})(CAMPER_STATES || {});
var CAMPER_MIGRATION_STATUS = /* @__PURE__ */ ((CAMPER_MIGRATION_STATUS2) => {
  CAMPER_MIGRATION_STATUS2["NOT_ELIGIBLE"] = "not_eligible";
  CAMPER_MIGRATION_STATUS2["ELIGIBLE_DUAL_NOT_DONE"] = "eligible_dual_not_done";
  CAMPER_MIGRATION_STATUS2["ELIGIBLE_MIGRATION_NOT_DONE"] = "eligible_migration_not_done";
  CAMPER_MIGRATION_STATUS2["DUAL_NOT_PUBLISHED"] = "dual_not_published";
  CAMPER_MIGRATION_STATUS2["DUAL_PUBLISHED"] = "dual_published";
  CAMPER_MIGRATION_STATUS2["MIGRATED_PUBLISHED"] = "migrated_published";
  CAMPER_MIGRATION_STATUS2["MIGRATED_NOT_PUBLISHED"] = "migrated_not_published";
  return CAMPER_MIGRATION_STATUS2;
})(CAMPER_MIGRATION_STATUS || {});
var DOCUMENT_STATES = /* @__PURE__ */ ((DOCUMENT_STATES2) => {
  DOCUMENT_STATES2["CREATED"] = "created";
  DOCUMENT_STATES2["ADDED"] = "added";
  DOCUMENT_STATES2["VALIDATION_ASKED"] = "valid_ask";
  DOCUMENT_STATES2["VALIDATED"] = "validated";
  DOCUMENT_STATES2["EXTERNAL_TRANSMISSION"] = "external_transmission";
  DOCUMENT_STATES2["NEEDDOC"] = "needdoc";
  DOCUMENT_STATES2["REFUSED"] = "refused";
  DOCUMENT_STATES2["REFUSED_MANGOPAY"] = "refuse_mgp";
  DOCUMENT_STATES2["EXPIRED_SOON"] = "exp_soon";
  DOCUMENT_STATES2["EXPIRED"] = "expired";
  return DOCUMENT_STATES2;
})(DOCUMENT_STATES || {});
var DOCUMENT_TYPES = /* @__PURE__ */ ((DOCUMENT_TYPES2) => {
  DOCUMENT_TYPES2["ADDRESS_PROOF"] = "addr_proof";
  DOCUMENT_TYPES2["COVID_PROOF"] = "covid_proof";
  DOCUMENT_TYPES2["REGISTRATION_PROOF"] = "reg_proof";
  DOCUMENT_TYPES2["REGISTRATION_ASSOCIATION"] = "reg_association";
  DOCUMENT_TYPES2["SHAREHOLDER_DECLARATION"] = "shareholder_decl";
  DOCUMENT_TYPES2["ID_PROOF"] = "id_proof";
  DOCUMENT_TYPES2["UBO_DECLARATION"] = "ubo_declaration";
  DOCUMENT_TYPES2["CAR_REGISTRATION"] = "car_registr";
  DOCUMENT_TYPES2["CAR_PROOF_INSURANCE"] = "car_proof_insu";
  DOCUMENT_TYPES2["DRIVING_LICENCE"] = "driving_licen";
  DOCUMENT_TYPES2["MOT"] = "MOT";
  DOCUMENT_TYPES2["IBAN"] = "iban";
  DOCUMENT_TYPES2["DVLA"] = "dvla";
  DOCUMENT_TYPES2["TAX_IDENTIFICATION_NUMBER"] = "tax_identification_number";
  DOCUMENT_TYPES2["RENTING_CONTRACT"] = "rent_cont";
  DOCUMENT_TYPES2["DETAIL_DECLARATION"] = "detail_decl";
  return DOCUMENT_TYPES2;
})(DOCUMENT_TYPES || {});
var USER_TYPES = /* @__PURE__ */ ((USER_TYPES2) => {
  USER_TYPES2["GUEST"] = "guest";
  USER_TYPES2["OWNER"] = "owner";
  return USER_TYPES2;
})(USER_TYPES || {});
var OWNER_ACCEPTANCE_STATES = /* @__PURE__ */ ((OWNER_ACCEPTANCE_STATES2) => {
  OWNER_ACCEPTANCE_STATES2["ON_DEMAND"] = "ON_DEMAND";
  OWNER_ACCEPTANCE_STATES2["ALLOWED"] = "ALLOWED";
  OWNER_ACCEPTANCE_STATES2["NOT_ALLOWED"] = "NOT_ALLOWED";
  return OWNER_ACCEPTANCE_STATES2;
})(OWNER_ACCEPTANCE_STATES || {});
var CAMPER_DELETE_REASONS = /* @__PURE__ */ ((CAMPER_DELETE_REASONS2) => {
  CAMPER_DELETE_REASONS2["DOUBLE_PRODUCT"] = "DELETE_DOUBLE";
  CAMPER_DELETE_REASONS2["VEHICLE_SOLD"] = "DELETE_SOLD";
  CAMPER_DELETE_REASONS2["WANT_TO_SOLD_VEHICLE"] = "DELETE_W_SOLD";
  CAMPER_DELETE_REASONS2["VEHICLE_KO"] = "DELETE_HS";
  CAMPER_DELETE_REASONS2["NOT_ENOUGH_BOOKINGS"] = "DELETE_NO_BK";
  CAMPER_DELETE_REASONS2["DISAGREE_YESCAPA"] = "DELETE_SITE";
  CAMPER_DELETE_REASONS2["BAD_EXPERIENCE_YESCAPA"] = "DELETE_BAD_EXP";
  CAMPER_DELETE_REASONS2["PERSONNAL_USE"] = "DELETE_PERSO";
  CAMPER_DELETE_REASONS2["OTHER"] = "REASON_NONE";
  return CAMPER_DELETE_REASONS2;
})(CAMPER_DELETE_REASONS || {});
var CAMPER_NON_AVAILABLE_REASONS = /* @__PURE__ */ ((CAMPER_NON_AVAILABLE_REASONS2) => {
  CAMPER_NON_AVAILABLE_REASONS2["PERSONAL_USE"] = "PERSONAL_USE";
  CAMPER_NON_AVAILABLE_REASONS2["OTHER_WEBSITE"] = "OTHER_WEBSITE";
  CAMPER_NON_AVAILABLE_REASONS2["BREAKDOWN"] = "BREAKDOWN";
  CAMPER_NON_AVAILABLE_REASONS2["WINTERING"] = "WINTERING";
  CAMPER_NON_AVAILABLE_REASONS2["OTHER"] = "OTHER";
  CAMPER_NON_AVAILABLE_REASONS2["GOBOONY_BOOKING"] = "GOBOONY_BOOKING";
  CAMPER_NON_AVAILABLE_REASONS2["GOBOONY_OWNER"] = "GOBOONY_OWNER";
  return CAMPER_NON_AVAILABLE_REASONS2;
})(CAMPER_NON_AVAILABLE_REASONS || {});
var CAMPER_PRICING_OPTION_RECURRENCES = /* @__PURE__ */ ((CAMPER_PRICING_OPTION_RECURRENCES2) => {
  CAMPER_PRICING_OPTION_RECURRENCES2["DAY"] = "day";
  CAMPER_PRICING_OPTION_RECURRENCES2["BOOKING"] = "booking";
  return CAMPER_PRICING_OPTION_RECURRENCES2;
})(CAMPER_PRICING_OPTION_RECURRENCES || {});
var BOOKING_KM_OPTIONS = /* @__PURE__ */ ((BOOKING_KM_OPTIONS2) => {
  BOOKING_KM_OPTIONS2[BOOKING_KM_OPTIONS2["UNLIMITED"] = 0] = "UNLIMITED";
  BOOKING_KM_OPTIONS2[BOOKING_KM_OPTIONS2["KM_100"] = 100] = "KM_100";
  BOOKING_KM_OPTIONS2[BOOKING_KM_OPTIONS2["KM_200"] = 200] = "KM_200";
  return BOOKING_KM_OPTIONS2;
})(BOOKING_KM_OPTIONS || {});
var SECOND_DRIVER_STATES = /* @__PURE__ */ ((SECOND_DRIVER_STATES2) => {
  SECOND_DRIVER_STATES2["SELECTED"] = "selected";
  SECOND_DRIVER_STATES2["NOT_SELECTED"] = "not_selected";
  SECOND_DRIVER_STATES2["INCLUDED"] = "included";
  SECOND_DRIVER_STATES2["NOT_AVAILABLE"] = "not_available";
  return SECOND_DRIVER_STATES2;
})(SECOND_DRIVER_STATES || {});
var BOOKING_STATES = /* @__PURE__ */ ((BOOKING_STATES2) => {
  BOOKING_STATES2["CREATED"] = "CREATED";
  BOOKING_STATES2["CREATED_PRO"] = "CREATED_PRO";
  BOOKING_STATES2["ABANDONED"] = "ABANDONED";
  BOOKING_STATES2["VALIDATED"] = "VALIDATED";
  BOOKING_STATES2["ACCEPTED"] = "ACCEPTED";
  BOOKING_STATES2["ACCEPTED_EXPIRED"] = "ACCEPTED_EXP";
  BOOKING_STATES2["ONGOING"] = "ONGOING";
  BOOKING_STATES2["TO_COME_NOT_PAID"] = "TO_COME_NOT_PAID";
  BOOKING_STATES2["TO_COME"] = "TO_COME";
  BOOKING_STATES2["DONE"] = "DONE";
  BOOKING_STATES2["CANCELLED_STAFF"] = "CANCELLED_STAFF";
  BOOKING_STATES2["CANCELLED_GUEST"] = "CANCELLED_GUEST";
  BOOKING_STATES2["CANCELLED_OWNER"] = "CANCELLED_OWNER";
  BOOKING_STATES2["CANCELLED_BOTH"] = "CANCELLED_BOTH";
  return BOOKING_STATES2;
})(BOOKING_STATES || {});
var BOOKING_PLAN_REFUND = /* @__PURE__ */ ((BOOKING_PLAN_REFUND2) => {
  BOOKING_PLAN_REFUND2["ON_TIME"] = "ON_TIME";
  BOOKING_PLAN_REFUND2["TOO_LATE"] = "TOO_LATE";
  return BOOKING_PLAN_REFUND2;
})(BOOKING_PLAN_REFUND || {});
var BOOKING_REQUEST_PURPOSES = /* @__PURE__ */ ((BOOKING_REQUEST_PURPOSES2) => {
  BOOKING_REQUEST_PURPOSES2["HOLIDAYS_FAMILY"] = "HOLIDAYS_FAMILY";
  BOOKING_REQUEST_PURPOSES2["HOLIDAYS_COUPLE"] = "HOLIDAYS_COUPLE";
  BOOKING_REQUEST_PURPOSES2["HOLIDAYS_FRIENDS"] = "HOLIDAYS_FRIENDS";
  BOOKING_REQUEST_PURPOSES2["RENT_BEFORE_BUY"] = "RENT_BEFORE_BUY";
  BOOKING_REQUEST_PURPOSES2["FAMILY_EVENT"] = "FAMILY_EVENT";
  BOOKING_REQUEST_PURPOSES2["SPORT_EVENT"] = "SPORT_EVENT";
  BOOKING_REQUEST_PURPOSES2["MUSICAL_EVENT"] = "MUSICAL_EVENT";
  BOOKING_REQUEST_PURPOSES2["OTHER"] = "OTHER";
  return BOOKING_REQUEST_PURPOSES2;
})(BOOKING_REQUEST_PURPOSES || {});
var BOOKING_EXTENSIONS_STATES = /* @__PURE__ */ ((BOOKING_EXTENSIONS_STATES2) => {
  BOOKING_EXTENSIONS_STATES2["CREATED"] = "CREATED";
  BOOKING_EXTENSIONS_STATES2["GUEST_ASKED"] = "GUEST_ASK";
  BOOKING_EXTENSIONS_STATES2["ACCEPTED"] = "ACCEPTED";
  BOOKING_EXTENSIONS_STATES2["CONFIRMED"] = "CONFIRMED";
  BOOKING_EXTENSIONS_STATES2["CANCELLED_OWNER"] = "CANCEL_OWNER";
  BOOKING_EXTENSIONS_STATES2["CANCELLED_GUEST"] = "CANCEL_GUEST";
  return BOOKING_EXTENSIONS_STATES2;
})(BOOKING_EXTENSIONS_STATES || {});
var BOOKING_EXTENSIONS_REASONS = /* @__PURE__ */ ((BOOKING_EXTENSIONS_REASONS2) => {
  BOOKING_EXTENSIONS_REASONS2["DAY_BEFORE"] = "day_before";
  BOOKING_EXTENSIONS_REASONS2["DAY_AFTER"] = "day_after";
  BOOKING_EXTENSIONS_REASONS2["KM_100"] = "km_100";
  BOOKING_EXTENSIONS_REASONS2["KM_200"] = "km_200";
  BOOKING_EXTENSIONS_REASONS2["KM_UNLIMITED"] = "km_unlimited";
  BOOKING_EXTENSIONS_REASONS2["DAY_SLIDE"] = "day_slide";
  return BOOKING_EXTENSIONS_REASONS2;
})(BOOKING_EXTENSIONS_REASONS || {});
var PAYMENT_STATES = /* @__PURE__ */ ((PAYMENT_STATES2) => {
  PAYMENT_STATES2["UNKNOWN"] = "unknow";
  PAYMENT_STATES2["SUGGESTED"] = "suggest";
  PAYMENT_STATES2["SELECTED"] = "select";
  PAYMENT_STATES2["TO_COME"] = "to_come";
  PAYMENT_STATES2["IN_PROGRESS"] = "in_prog";
  PAYMENT_STATES2["DONE"] = "done";
  PAYMENT_STATES2["FAILED"] = "failed";
  PAYMENT_STATES2["CANCELLED"] = "cancel";
  return PAYMENT_STATES2;
})(PAYMENT_STATES || {});
var PAYMENT_TYPES = /* @__PURE__ */ ((PAYMENT_TYPES2) => {
  PAYMENT_TYPES2["UNKNOWN"] = "unknow";
  PAYMENT_TYPES2["CARD"] = "card";
  PAYMENT_TYPES2["CARD_2X"] = "card_2";
  PAYMENT_TYPES2["CREDIT_4X"] = "cred_4";
  PAYMENT_TYPES2["HOLIDAY_VOUCHERS"] = "ho_vou";
  PAYMENT_TYPES2["TRANSFER"] = "transf";
  PAYMENT_TYPES2["SOFORT"] = "sofort";
  PAYMENT_TYPES2["MONEYBOX"] = "moneyb";
  PAYMENT_TYPES2["PAYPAL"] = "paypal";
  PAYMENT_TYPES2["KLARNA"] = "klarna";
  PAYMENT_TYPES2["IDEAL"] = "ideal";
  PAYMENT_TYPES2["MBWAY"] = "mbway";
  return PAYMENT_TYPES2;
})(PAYMENT_TYPES || {});
var PAYMENT_REASONS = /* @__PURE__ */ ((PAYMENT_REASONS2) => {
  PAYMENT_REASONS2["UNKNOWN"] = "unknow";
  PAYMENT_REASONS2["REASON_BOOKING"] = "book";
  PAYMENT_REASONS2["REASON_EXTENTION"] = "exten";
  PAYMENT_REASONS2["REASON_EXTENTION_GROUP"] = "exteng";
  PAYMENT_REASONS2["REASON_SINISTER"] = "sinist";
  PAYMENT_REASONS2["REASON_GIFT_CARD"] = "gftcrd";
  PAYMENT_REASONS2["REASON_DEPOSIT"] = "depos";
  PAYMENT_REASONS2["REASON_GOODWILL"] = "gest";
  PAYMENT_REASONS2["REASON_REFUND"] = "refund";
  PAYMENT_REASONS2["REASON_MONEYBOX"] = "moneyb";
  PAYMENT_REASONS2["REASON_TOLLS_AND_FINES"] = "tolls";
  return PAYMENT_REASONS2;
})(PAYMENT_REASONS || {});
var PAYMENT_MBWAY_STATUS = /* @__PURE__ */ ((PAYMENT_MBWAY_STATUS2) => {
  PAYMENT_MBWAY_STATUS2["CREATED"] = "CREATED";
  PAYMENT_MBWAY_STATUS2["SUCCEEDED"] = "SUCCEEDED";
  PAYMENT_MBWAY_STATUS2["FAILED"] = "FAILED";
  return PAYMENT_MBWAY_STATUS2;
})(PAYMENT_MBWAY_STATUS || {});
var BOOKING_PRICE_ERROR_CODES = /* @__PURE__ */ ((BOOKING_PRICE_ERROR_CODES2) => {
  BOOKING_PRICE_ERROR_CODES2["INVERTED_DATES"] = "103";
  BOOKING_PRICE_ERROR_CODES2["INVALID_DOCUMENTS_LESS_72"] = "104";
  BOOKING_PRICE_ERROR_CODES2["KM_UNLIMITED_NOT_ALLOWED"] = "106";
  BOOKING_PRICE_ERROR_CODES2["NOT_AVAILABLE"] = "107";
  BOOKING_PRICE_ERROR_CODES2["MIN_DAYS_REQUIRED"] = "108";
  BOOKING_PRICE_ERROR_CODES2["CLOSED_DAYS"] = "109";
  BOOKING_PRICE_ERROR_CODES2["INVALID_DOCUMENTS_LESS_24"] = "110";
  return BOOKING_PRICE_ERROR_CODES2;
})(BOOKING_PRICE_ERROR_CODES || {});
var BOOKING_UPGRADES_STATES = /* @__PURE__ */ ((BOOKING_UPGRADES_STATES2) => {
  BOOKING_UPGRADES_STATES2["CREATED"] = "CREATED";
  BOOKING_UPGRADES_STATES2["ACCEPTED"] = "ACCEPTED";
  BOOKING_UPGRADES_STATES2["CONFIRMED"] = "CONFIRMED";
  BOOKING_UPGRADES_STATES2["CANCELLED_GUEST"] = "CANCEL_GUEST";
  return BOOKING_UPGRADES_STATES2;
})(BOOKING_UPGRADES_STATES || {});
var BOOKING_UPGRADES_REASONS = /* @__PURE__ */ ((BOOKING_UPGRADES_REASONS2) => {
  BOOKING_UPGRADES_REASONS2["BUYBACK"] = "buyback";
  BOOKING_UPGRADES_REASONS2["RENTAL_COVER"] = "rental_cover";
  BOOKING_UPGRADES_REASONS2["SECOND_DRIVER"] = "second_driver";
  return BOOKING_UPGRADES_REASONS2;
})(BOOKING_UPGRADES_REASONS || {});
var MANGOPAY_UBO_DECLARATION_STATUS = /* @__PURE__ */ ((MANGOPAY_UBO_DECLARATION_STATUS2) => {
  MANGOPAY_UBO_DECLARATION_STATUS2["C"] = "created";
  MANGOPAY_UBO_DECLARATION_STATUS2["A"] = "valid_ask";
  MANGOPAY_UBO_DECLARATION_STATUS2["V"] = "validated";
  MANGOPAY_UBO_DECLARATION_STATUS2["I"] = "refused";
  MANGOPAY_UBO_DECLARATION_STATUS2["R"] = "refused";
  return MANGOPAY_UBO_DECLARATION_STATUS2;
})(MANGOPAY_UBO_DECLARATION_STATUS || {});
const PRICES_COLORS = [
  { value: 0, name: "DEFAULT", hex: "#e4e4e7" },
  { value: 1, name: "blue", hex: "#BFDBFE" },
  { value: 2, name: "orange", hex: "#FDBA74" },
  { value: 3, name: "yellow", hex: "#FDE68A" },
  { value: 4, name: "pink", hex: "#FBCFE8" },
  { value: 5, name: "purple", hex: "#DDD6FE" },
  { value: 6, name: "green", hex: "#A1FDBE" }
];
const PRICES_COLOR_DEFAULT = { value: 0, name: "DEFAULT", hex: "#e4e4e7" };
const PRICES_DEPOSIT_MEAN = ["card", "check", "cash", "transfer", "others"];
const OWNER_MANAGED_INSURANCE_SLUG = "none";
var VEHICLE_PICTURES = /* @__PURE__ */ ((VEHICLE_PICTURES2) => {
  VEHICLE_PICTURES2[VEHICLE_PICTURES2["MIN_WIDTH"] = 638] = "MIN_WIDTH";
  VEHICLE_PICTURES2[VEHICLE_PICTURES2["MIN_HEIGHT"] = 426] = "MIN_HEIGHT";
  VEHICLE_PICTURES2[VEHICLE_PICTURES2["MAX_SIZE"] = 10] = "MAX_SIZE";
  return VEHICLE_PICTURES2;
})(VEHICLE_PICTURES || {});
var VEHICLE_AVAILABILITY_STATES = /* @__PURE__ */ ((VEHICLE_AVAILABILITY_STATES2) => {
  VEHICLE_AVAILABILITY_STATES2["AVAILABLE"] = "Available";
  VEHICLE_AVAILABILITY_STATES2["NOT_AVAILABLE"] = "NotAvailable";
  return VEHICLE_AVAILABILITY_STATES2;
})(VEHICLE_AVAILABILITY_STATES || {});
var VEHICLE_DRIVING_LICENSE = /* @__PURE__ */ ((VEHICLE_DRIVING_LICENSE2) => {
  VEHICLE_DRIVING_LICENSE2["B"] = "B";
  VEHICLE_DRIVING_LICENSE2["C"] = "C";
  return VEHICLE_DRIVING_LICENSE2;
})(VEHICLE_DRIVING_LICENSE || {});
var BOOKING_PAYMENT_METHODS = /* @__PURE__ */ ((BOOKING_PAYMENT_METHODS2) => {
  BOOKING_PAYMENT_METHODS2["CREDIT_CARD"] = "card";
  BOOKING_PAYMENT_METHODS2["CREDIT_CARD_2x"] = "card_2";
  BOOKING_PAYMENT_METHODS2["CREDIT_CARD_4x"] = "cred_4";
  BOOKING_PAYMENT_METHODS2["KLARNA"] = "sofort";
  BOOKING_PAYMENT_METHODS2["PAYPAL"] = "paypal";
  BOOKING_PAYMENT_METHODS2["IDEAL"] = "ideal";
  BOOKING_PAYMENT_METHODS2["MBWAY"] = "mbway";
  return BOOKING_PAYMENT_METHODS2;
})(BOOKING_PAYMENT_METHODS || {});
var CAMPER_BOOKING_PRICE_SEASON_TYPES = /* @__PURE__ */ ((CAMPER_BOOKING_PRICE_SEASON_TYPES2) => {
  CAMPER_BOOKING_PRICE_SEASON_TYPES2[CAMPER_BOOKING_PRICE_SEASON_TYPES2["LOW_SEASON"] = 1] = "LOW_SEASON";
  CAMPER_BOOKING_PRICE_SEASON_TYPES2[CAMPER_BOOKING_PRICE_SEASON_TYPES2["HIGH_SEASON"] = 3] = "HIGH_SEASON";
  return CAMPER_BOOKING_PRICE_SEASON_TYPES2;
})(CAMPER_BOOKING_PRICE_SEASON_TYPES || {});
const entriesFromObject = (o) => {
  if (!isObject(o)) {
    return [];
  }
  return Object.entries(o);
};
const fromObjectToURLSearchParams = (obj, _searchParams) => {
  const searchParams = _searchParams ?? new URLSearchParams();
  entriesFromObject(obj).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((v) => fromObjectToURLSearchParams({ [key]: v }, searchParams));
      return;
    }
    if (isString(value)) {
      searchParams.append(key, value);
    } else if (isDate$1(value)) {
      searchParams.append(key, formatDateISOWithoutTime({ date: value }));
    } else if (value === true || value === false || !isNaN(value)) {
      searchParams.append(key, `${value}`);
    }
  });
  return searchParams;
};
const getRequestOptions = ({ url, method, body }) => ({
  url: url.href,
  options: {
    method,
    body
  }
});
const isDate = (date) => {
  return Boolean(date) && !isNaN(date) && typeof date.getMonth === "function";
};
const isObject = (x) => {
  return typeof x === "object" && !Array.isArray(x) && x !== null;
};
const mapValueText = (entry) => ({ value: entry[0], text: entry[1] });
const reduceDoubleArray = (acc, cur) => {
  acc[cur[0]] = cur[1];
  return acc;
};
const setURLSearchParams = ({ url, query = void 0 }) => {
  if (!query) {
    return url;
  }
  const querySearchParams = fromObjectToURLSearchParams(query);
  querySearchParams.forEach((value, key) => {
    url.searchParams.append(key, value);
  });
  return url;
};
const stringToArray = (value) => Array.isArray(value) ? value : isString(value) ? value.split(/\s?,\s?/g).filter((s) => s.length) : [];
var APIVehicleFuelEnum = /* @__PURE__ */ ((APIVehicleFuelEnum2) => {
  APIVehicleFuelEnum2[APIVehicleFuelEnum2["Gasoline"] = 1] = "Gasoline";
  APIVehicleFuelEnum2[APIVehicleFuelEnum2["Diesel"] = 2] = "Diesel";
  APIVehicleFuelEnum2[APIVehicleFuelEnum2["LPG"] = 3] = "LPG";
  APIVehicleFuelEnum2[APIVehicleFuelEnum2["Electric"] = 4] = "Electric";
  APIVehicleFuelEnum2[APIVehicleFuelEnum2["Other"] = 5] = "Other";
  return APIVehicleFuelEnum2;
})(APIVehicleFuelEnum || {});
var APIVehicleGearEnum = /* @__PURE__ */ ((APIVehicleGearEnum2) => {
  APIVehicleGearEnum2[APIVehicleGearEnum2["Manual"] = 1] = "Manual";
  APIVehicleGearEnum2[APIVehicleGearEnum2["Automatic"] = 2] = "Automatic";
  return APIVehicleGearEnum2;
})(APIVehicleGearEnum || {});
export {
  APIVehicleFuelEnum,
  APIVehicleGearEnum,
  BOOKING_EXTENSIONS_REASONS,
  BOOKING_EXTENSIONS_STATES,
  BOOKING_GUEST_CANCEL_REASONS,
  BOOKING_HOUR_FROM_AFTERNOON,
  BOOKING_HOUR_FROM_MORNING,
  BOOKING_HOUR_TO_AFTERNOON,
  BOOKING_HOUR_TO_MORNING,
  BOOKING_KM_OPTIONS,
  BOOKING_META_STATES,
  BOOKING_OWNER_CANCEL_REASONS,
  BOOKING_OWNER_CANCEL_STATE,
  BOOKING_OWNER_REFUSE_REASONS,
  BOOKING_PAYMENT_METHODS,
  BOOKING_PLAN_REFUND,
  BOOKING_PRICE_ERROR_CODES,
  BOOKING_REQUEST_PURPOSES,
  BOOKING_STATES,
  BOOKING_UPGRADES_REASONS,
  BOOKING_UPGRADES_STATES,
  CAMPER_BOOKING_PRICE_SEASON_TYPES,
  CAMPER_COMPLETION_TYPES,
  CAMPER_DELETE_REASONS,
  CAMPER_MIGRATION_STATUS,
  CAMPER_NON_AVAILABLE_REASONS,
  CAMPER_PRICING_OPTION_RECURRENCES,
  CAMPER_STATES,
  CAMPER_SUSPEND_REASONS,
  CAMPER_TYPES,
  CAMPER_TYPES_INTEGER,
  DEPOSIT_MEANS,
  DOCUMENT_STATES,
  DOCUMENT_TYPES,
  MANGOPAY_UBO_DECLARATION_STATUS,
  OWNER_ACCEPTANCE_STATES,
  OWNER_MANAGED_INSURANCE_SLUG,
  PAYMENT_MBWAY_STATUS,
  PAYMENT_REASONS,
  PAYMENT_STATES,
  PAYMENT_TYPES,
  PRICES_COLORS,
  PRICES_COLOR_DEFAULT,
  PRICES_DEPOSIT_MEAN,
  REFUND_REASONS,
  RequestCreateBookingGuest,
  RequestCreateBookingGuestExtensions,
  RequestCreateBookingGuestPayment,
  RequestCreateBookingGuestPaymentAsync,
  RequestCreateBookingGuestPaymentConfirmation,
  RequestCreateBookingGuestUpgrades,
  RequestCreateCamperPeriod,
  RequestCreateCamperPrice,
  RequestCreateCamperPricingOption,
  RequestCreateCamperReactivation,
  RequestCreateChatMessage,
  RequestCreateDocumentPage,
  RequestCreateEarningsSimulation,
  RequestCreateHubspotPushLeadAdOnboarding,
  RequestCreateIban,
  RequestCreateInsuranceConditions,
  RequestCreateLogAsCredentials,
  RequestCreateLoginCredentials,
  RequestCreateLogout,
  RequestCreateNavigationEvent,
  RequestCreateNewsletterRegistration,
  RequestCreatePasswordReset,
  RequestCreatePhoneCertification,
  RequestCreateProfilePicture,
  RequestCreateRefreshCredentials,
  RequestCreateReview,
  RequestCreateUBODeclarationUser,
  RequestCreateUser,
  RequestCreateUserCompany,
  RequestCreateUserLegalOwner,
  RequestCreateVehicle,
  RequestCreateVehiclePhoto,
  RequestCreateVehicleSleeping,
  RequestCreateWishlist,
  RequestCreateWishlistCamper,
  RequestDeleteBookingGuestExtensions,
  RequestDeleteBookingGuestUpgrades,
  RequestDeleteCamper,
  RequestDeleteCamperPeriod,
  RequestDeleteCamperPrice,
  RequestDeleteCamperPricingOption,
  RequestDeleteDocumentPage,
  RequestDeleteUserMe,
  RequestDeleteVehiclePhoto,
  RequestDeleteVehicleSleeping,
  RequestDeleteWishlist,
  RequestDeleteWishlistCamper,
  RequestReadBookingGuest,
  RequestReadBookingGuestCheckoutInfos,
  RequestReadBookingGuestExtensionsPaymentInfos,
  RequestReadBookingGuestPayment,
  RequestReadBookingGuestPaymentAsync,
  RequestReadBookingGuestPaymentFinalization,
  RequestReadBookingGuestPaymentMethods,
  RequestReadBookingGuestPaymentRedirection,
  RequestReadBookingGuestPrices,
  RequestReadBookingGuestRefund,
  RequestReadBookingGuestUpgrades,
  RequestReadBookingOwner,
  RequestReadBookingOwnerPrices,
  RequestReadBookingRequestPrice,
  RequestReadBookingsGuest,
  RequestReadBookingsGuestFilters,
  RequestReadBookingsOwner,
  RequestReadBookingsOwnerFilters,
  RequestReadBrands,
  RequestReadBrandsModels,
  RequestReadCamper,
  RequestReadCamperAvailabilities,
  RequestReadCamperPeriods,
  RequestReadCamperPrices,
  RequestReadCamperPricing,
  RequestReadCamperProgress,
  RequestReadCamperStatistics,
  RequestReadCampers,
  RequestReadCampersOfMine,
  RequestReadCampersOfMineFilters,
  RequestReadCampersSearch,
  RequestReadChat,
  RequestReadChats,
  RequestReadDocument,
  RequestReadDocuments,
  RequestReadFAQs,
  RequestReadFAQsByUserType,
  RequestReadGeolocations,
  RequestReadGoboonyDuplicationRedirecUrl,
  RequestReadIban,
  RequestReadInsurance,
  RequestReadInsuranceConditions,
  RequestReadInsuranceRentalCover,
  RequestReadInsurances,
  RequestReadInsurancesSimilar,
  RequestReadLeadSource,
  RequestReadManufacturers,
  RequestReadManufacturersModels,
  RequestReadMoneyboxes,
  RequestReadPayins,
  RequestReadPayouts,
  RequestReadPhoneCertification,
  RequestReadReferential,
  RequestReadReview,
  RequestReadReviews,
  RequestReadReviewsNeeded,
  RequestReadRouting,
  RequestReadSearchLocationInfos,
  RequestReadSearchLocations,
  RequestReadStatistics,
  RequestReadUBODeclaration,
  RequestReadUBODeclarationUsers,
  RequestReadUser,
  RequestReadUserCompany,
  RequestReadUserLegalOwner,
  RequestReadUserMe,
  RequestReadVehicle,
  RequestReadVehicleEquipments,
  RequestReadVehiclePhotos,
  RequestReadVehicleSleepings,
  RequestReadWishlist,
  RequestReadWishlists,
  RequestReadYesboonySearch,
  RequestUpdateBookingGuest,
  RequestUpdateBookingGuestCancellation,
  RequestUpdateBookingGuestExtensionsCancellation,
  RequestUpdateBookingGuestExtensionsValidation,
  RequestUpdateBookingGuestPrices,
  RequestUpdateBookingGuestUpgradesCancellation,
  RequestUpdateBookingGuestUpgradesValidation,
  RequestUpdateBookingGuestValidation,
  RequestUpdateBookingOwnerApproval,
  RequestUpdateBookingOwnerExtensionApproval,
  RequestUpdateBookingOwnerExtensionRefusal,
  RequestUpdateBookingOwnerRefusal,
  RequestUpdateCamper,
  RequestUpdateCamperPeriod,
  RequestUpdateCamperPrice,
  RequestUpdateCamperPricing,
  RequestUpdateCamperPricingOption,
  RequestUpdateChatMessage,
  RequestUpdateDocument,
  RequestUpdateInsuranceConditions,
  RequestUpdateReview,
  RequestUpdateReviewPhoto,
  RequestUpdateReviewReport,
  RequestUpdateUBODeclaration,
  RequestUpdateUBODeclarationUser,
  RequestUpdateUserCompany,
  RequestUpdateUserLegalOwner,
  RequestUpdateUserMe,
  RequestUpdateVehicle,
  RequestUpdateVehicleEquipments,
  RequestUpdateVehiclePhoto,
  RequestUpdateVehiclePhotoRotation,
  RequestUpdateWishlist,
  SECOND_DRIVER_STATES,
  TRACKING_EVENT_TYPES,
  USER_PROFESIONAL_TYPES,
  USER_TYPES,
  VEHICLE_AVAILABILITY_STATES,
  VEHICLE_DRIVING_LICENSE,
  VEHICLE_PICTURES,
  YscApiConsumer,
  YscApiRequest,
  YscApiRequestCreate,
  YscApiRequestCreateFormUrlEncoded,
  YscApiRequestCreateMultipartFormData,
  YscApiRequestDelete,
  YscApiRequestRead,
  YscApiRequestUpdate,
  YscApiRequestUpdateMultipartFormData,
  YscApiRequestWithPayload,
  YscApiRequestWithQuery,
  arrayToStringOrNull,
  computeCountryOptions,
  entriesFromObject,
  formatDateISOWithTimezone,
  formatDateISOWithoutTime,
  formatDateISOWithoutTimezone,
  fromObjectToURLSearchParams,
  getRequestOptions,
  isDate,
  isObject,
  isString,
  mapValueText,
  reduceDoubleArray,
  setURLSearchParams,
  stringToArray
};
